import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  QueryConstraint,
  Timestamp,
  addDoc,
  serverTimestamp,
  runTransaction,
  increment,
  onSnapshot,
  DocumentSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, deleteObject } from "firebase/storage";
import { db, storage, functions } from "./firebase";
import { generateReservationCode } from "./utils";
import type {
  User,
  InsertUser,
  Vehicle,
  InsertVehicle,
  Service,
  InsertService,
  Appointment,
  InsertAppointment,
  Wash,
  InsertWash,
  EmployeeProfile,
  InsertEmployeeProfile,
  EmployeePayment,
  InsertEmployeePayment,
  Coupon,
  InsertCoupon,
  Expense,
  InsertExpense,
  FixedExpense,
  InsertFixedExpense,
  Review,
  InsertReview,
  CorporateQuote,
  InsertCorporateQuote,
  CorporateCompany,
  InsertCorporateCompany,
  CorporatePayment,
  InsertCorporatePayment,
  CorporateDebt,
  InsertCorporateDebt,
  Payment,
  InsertPayment,
  CustomerLoyalty,
  InsertCustomerLoyalty,
  LoyaltyRedemption,
  InsertLoyaltyRedemption,
  RewardCoupon,
  InsertRewardCoupon,
  SiteSettings,
  InsertSiteSettings,
  SystemSettings,
  InsertSystemSettings,
  NotificationTemplate,
  InsertNotificationTemplate,
  NotificationSound as NotificationSoundType,
  InsertNotificationSound,
  TemplateVersion,
  InsertTemplateVersion,
  Gift,
  InsertGift,
  InventoryItem,
  InsertInventoryItem,
  StockMovement,
  InsertStockMovement,
  BrandSettings,
  InsertBrandSettings,
  Faq,
  InsertFaq,
  Feature,
  InsertFeature,
  HomePageSection,
  InsertHomePageSection,
  CorporatePricing,
  InsertCorporatePricing,
  CorporateInvoice,
  InsertCorporateInvoice,
  Testimonial,
  InsertTestimonial,
  Popup,
  InsertPopup,
  ContactSettings,
  InsertContactSettings,
  GalleryItem,
  InsertGalleryItem,
  GalleryPageSettings,
  InsertGalleryPageSettings,
  GalleryCategory_Type,
  InsertGalleryCategory,
  BlogPost,
  InsertBlogPost,
  DeploymentSettings,
  InsertDeploymentSettings,
  UserNotification,
  InsertUserNotification,
  CustomerAnalytics,
  InsertCustomerAnalytics,
  CorporateAgreement,
  InsertCorporateAgreement,
  VehicleAnalytics,
  InsertVehicleAnalytics,
  SubscriptionPackage,
  InsertSubscriptionPackage,
  CustomerSubscription,
  InsertCustomerSubscription,
  SubscriptionUsage,
  InsertSubscriptionUsage,
  CorporateBudget,
  InsertCorporateBudget,
  CouponUsageLog,
  InsertCouponUsageLog,
} from "@shared/schema";
import { LOYALTY_CONFIG } from "@shared/schema";
import { 
  createWashFromAppointmentTransaction,
  completeWashWithLoyaltyTransaction,
  updateAnalyticsAfterWashTransaction
} from "@shared/helpers/firestoreTransactions";

// Collection references
export const COLLECTIONS = {
  USERS: "users",
  VEHICLES: "vehicles",
  SERVICES: "services",
  APPOINTMENTS: "appointments",
  WASHES: "washes",
  EMPLOYEES: "employees",
  EMPLOYEE_PAYMENTS: "employeePayments",
  COUPONS: "coupons",
  EXPENSES: "expenses",
  FIXED_EXPENSES: "fixedExpenses",
  REVIEWS: "reviews",
  CORPORATE_QUOTES: "corporateQuotes",
  CORPORATE_COMPANIES: "corporateCompanies",
  CORPORATE_PAYMENTS: "corporatePayments",
  CORPORATE_DEBTS: "corporateDebts",
  PAYMENTS: "payments",
  LOYALTY: "customerLoyalty",
  LOYALTY_REDEMPTIONS: "loyaltyRedemptions",
  REWARD_COUPONS: "rewardCoupons",
  NOTIFICATION_TEMPLATES: "notificationTemplates",
  NOTIFICATION_SOUNDS: "notificationSounds",
  TEMPLATE_VERSIONS: "templateVersions",
  SITE_SETTINGS: "siteSettings",
  SYSTEM_SETTINGS: "systemSettings",
  FEEDBACK: "feedback",
  GIFTS: "gifts",
  // Web Builder Collections
  TESTIMONIALS: "testimonials",
  CONTACT_SETTINGS: "contactSettings",
  GALLERY: "gallery",
  GALLERY_CATEGORIES: "galleryCategories",
  BLOG_POSTS: "blogPosts",
  SMS_LOGS: "smsLogs",
  SMS_TEMPLATES: "smsTemplates",
  SMS_SETTINGS: "smsSettings",
  CAMPAIGN_SMS_TEMPLATES: "campaignSmsTemplates",
  INVENTORY: "inventory",
  STOCK_MOVEMENTS: "stockMovements",
  POPUPS: "popups",
  EMAIL_TEMPLATES: "emailTemplates",
  EMAIL_LOGS: "emailLogs",
  EMAIL_SETTINGS: "emailSettings",
  // Web Builder Collections
  BRAND_SETTINGS: "brandSettings",
  FAQS: "faqs",
  FEATURES: "features",
  HOME_PAGE_SECTIONS: "homePageSections",
  // Corporate Management
  CORPORATE_PRICING: "corporatePricing",
  CORPORATE_INVOICES: "corporateInvoices",
  // Deployment Settings
  DEPLOYMENT_SETTINGS: "deploymentSettings",
  // Coupon Usage Log
  COUPON_USAGE_LOG: "couponUsageLog",
  // User Notifications (subcollection: users/{userId}/notifications)
  NOTIFICATIONS: "notifications",
  // Analytics Collections
  CUSTOMER_ANALYTICS: "customerAnalytics",
  CORPORATE_AGREEMENTS: "corporateAgreements",
  VEHICLE_ANALYTICS: "vehicleAnalytics",
} as const;

// Helper to convert Firestore timestamps to numbers
function convertTimestamps(data: any): any {
  if (!data) return data;
  
  const converted = { ...data };
  Object.keys(converted).forEach(key => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toMillis();
    }
  });
  return converted;
}

// ============================================================================
// USERS
// ============================================================================

export async function createUser(userId: string, data: InsertUser): Promise<User> {
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  const userData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  await setDoc(userRef, userData);
  
  const created = await getDoc(userRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as User;
}

export async function getUser(userId: string): Promise<User | null> {
  console.log("👤 [getUser] Fetching user:", userId);
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    console.warn("⚠️ [getUser] User not found:", userId);
    return null;
  }
  const user = { id: userDoc.id, ...convertTimestamps(userDoc.data()) } as User;
  console.log("✅ [getUser] User found:", { id: user.id, displayName: user.displayName });
  return user;
}

export async function updateUser(userId: string, data: Partial<InsertUser>): Promise<void> {
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  await updateDoc(userRef, data);
}

export async function getAllUsers(limitCount: number = 500): Promise<User[]> {
  const q = query(
    collection(db, COLLECTIONS.USERS), 
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as User[];
}

export async function getUserByPhone(phone: string): Promise<User | null> {
  console.log('[getUserByPhone] Searching for user with phone:', phone);
  
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where("phone", "==", phone)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('[getUserByPhone] No user found with phone:', phone);
      return null;
    }
    
    const userDoc = snapshot.docs[0];
    const user = { id: userDoc.id, ...convertTimestamps(userDoc.data()) } as User;
    console.log('[getUserByPhone] User found:', { id: user.id, displayName: user.displayName });
    return user;
  } catch (error) {
    console.error('[getUserByPhone] Error:', error);
    return null;
  }
}

// ============================================================================
// PLATE-BASED VEHICLE/USER LOOKUP
// ============================================================================

export interface VehicleWithOwner {
  vehicle: Vehicle;
  owner: User | null;
}

/**
 * Find vehicle by normalized plate and get its owner if registered
 * This is the primary method for linking walk-in washes to registered customers
 */
export async function getVehicleByPlate(plate: string): Promise<VehicleWithOwner | null> {
  // Normalize plate for consistent matching
  const normalizedPlate = plate.trim().toUpperCase().replace(/\s+/g, "");
  console.log('[getVehicleByPlate] Searching for plate:', normalizedPlate);
  
  try {
    // Query on "plate" field (actual field name in vehicles collection)
    let q = query(
      collection(db, COLLECTIONS.VEHICLES),
      where("plate", "==", normalizedPlate)
    );
    
    let snapshot = await getDocs(q);
    
    // If not found, try original plate format (for legacy data with spaces)
    if (snapshot.empty) {
      q = query(
        collection(db, COLLECTIONS.VEHICLES),
        where("plate", "==", plate.trim().toUpperCase())
      );
      snapshot = await getDocs(q);
    }
    
    if (snapshot.empty) {
      console.log('[getVehicleByPlate] No vehicle found with plate:', normalizedPlate);
      return null;
    }
    
    // If multiple vehicles with same plate exist, prefer one with userId
    let vehicleDoc = snapshot.docs[0];
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.userId) {
        vehicleDoc = doc;
        break;
      }
    }
    
    const vehicle = { id: vehicleDoc.id, ...convertTimestamps(vehicleDoc.data()) } as Vehicle;
    console.log('[getVehicleByPlate] Vehicle found:', { id: vehicle.id, plate: vehicle.plate, userId: vehicle.userId });
    
    // Get owner if userId exists
    let owner: User | null = null;
    if (vehicle.userId) {
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, vehicle.userId));
        if (userDoc.exists()) {
          owner = { id: userDoc.id, ...convertTimestamps(userDoc.data()) } as User;
          console.log('[getVehicleByPlate] Owner found:', { id: owner.id, displayName: owner.displayName });
        }
      } catch (err) {
        console.warn('[getVehicleByPlate] Could not fetch owner:', err);
      }
    }
    
    return { vehicle, owner };
  } catch (error) {
    console.error('[getVehicleByPlate] Error:', error);
    return null;
  }
}

/**
 * Find vehicle by plate only (without owner lookup)
 * Lightweight version for simple plate checks
 */
export async function getVehicleByPlateOnly(plate: string): Promise<Vehicle | null> {
  const normalizedPlate = plate.trim().toUpperCase().replace(/\s+/g, "");
  
  try {
    let q = query(
      collection(db, COLLECTIONS.VEHICLES),
      where("plate", "==", normalizedPlate)
    );
    
    let snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      q = query(
        collection(db, COLLECTIONS.VEHICLES),
        where("plate", "==", plate.trim().toUpperCase())
      );
      snapshot = await getDocs(q);
    }
    
    if (snapshot.empty) {
      return null;
    }
    
    // Prefer vehicle with userId
    let vehicleDoc = snapshot.docs[0];
    for (const docItem of snapshot.docs) {
      const data = docItem.data();
      if (data.userId) {
        vehicleDoc = docItem;
        break;
      }
    }
    
    return { id: vehicleDoc.id, ...convertTimestamps(vehicleDoc.data()) } as Vehicle;
  } catch (error) {
    console.error('[getVehicleByPlateOnly] Error:', error);
    return null;
  }
}

/**
 * Update vehicle to assign owner (when linking guest vehicle to registered user)
 */
export async function assignVehicleOwner(vehicleId: string, userId: string): Promise<void> {
  console.log('[assignVehicleOwner] Assigning vehicle', vehicleId, 'to user', userId);
  
  try {
    await updateDoc(doc(db, COLLECTIONS.VEHICLES, vehicleId), {
      userId: userId,
      updatedAt: serverTimestamp(),
    });
    console.log('[assignVehicleOwner] Vehicle owner updated successfully');
  } catch (error) {
    console.error('[assignVehicleOwner] Error:', error);
    throw error;
  }
}

export async function getUsersPaginated(pageSize: number = 50, lastDoc?: DocumentSnapshot): Promise<{
  users: User[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const constraints: QueryConstraint[] = [
    orderBy("createdAt", "desc"),
    limit(pageSize + 1), // Fetch one extra to check if there are more
  ];
  
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }
  
  const q = query(collection(db, COLLECTIONS.USERS), ...constraints);
  const snapshot = await getDocs(q);
  
  const hasMore = snapshot.docs.length > pageSize;
  const users = snapshot.docs
    .slice(0, pageSize)
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as User[];
  
  return {
    users,
    lastDoc: snapshot.docs[pageSize - 1] || null,
    hasMore,
  };
}

export async function getEmployeeUsers(): Promise<User[]> {
  // Import UserRole at the top if needed, but we'll use string values directly
  const q = query(
    collection(db, COLLECTIONS.USERS),
    where("role", "in", ["employee", "cashier", "admin"]),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as User[];
}

// ============================================================================
// VEHICLES
// ============================================================================

export async function createVehicle(data: InsertVehicle): Promise<Vehicle> {
  try {
    console.log("[createVehicle] Starting with data:", data);
    
    // CRITICAL: Use plate as document ID for atomic uniqueness
    const normalizedPlate = data.plate.toUpperCase().trim().replace(/\s/g, ''); // Remove spaces for ID
    const vehicleRef = doc(db, COLLECTIONS.VEHICLES, normalizedPlate);
    
    // CRITICAL: Use Firestore transaction for atomic read-check-write
    const result = await runTransaction(db, async (transaction) => {
      const existingDoc = await transaction.get(vehicleRef);
      
      if (existingDoc.exists()) {
        const existingVehicle = { id: existingDoc.id, ...convertTimestamps(existingDoc.data()) } as Vehicle;
        console.log("[createVehicle] 🔍 Plate already exists:", existingVehicle.id);
        
        // CRITICAL: If vehicle belongs to another registered user, REJECT
        if (existingVehicle.userId && existingVehicle.userId !== data.userId) {
          console.error("[createVehicle] ❌ CONFLICT: Plate already belongs to another user!", {
            existingUserId: existingVehicle.userId,
            requestUserId: data.userId
          });
          throw new Error(`Bu plaka (${data.plate}) sistemde başka bir kullanıcıya kayıtlı.`);
        }
        
        // If existing vehicle is guest (userId: null) and new data has userId, upgrade it
        if (!existingVehicle.userId && data.userId) {
          console.log("[createVehicle] ⬆️ UPGRADING guest vehicle to registered user:", data.userId);
          transaction.update(vehicleRef, {
            userId: data.userId,
            brand: data.brand,
            model: data.model,
            color: data.color,
            type: data.type,
            updatedAt: serverTimestamp(),
          });
          
          console.log("[createVehicle] ✅ Guest vehicle upgrade queued");
          return { ...existingVehicle, userId: data.userId, brand: data.brand, model: data.model, color: data.color, type: data.type };
        }
        
        // If same user, allow companyId update (admin or owner)
        if (data.userId === existingVehicle.userId && data.companyId && existingVehicle.companyId !== data.companyId) {
          console.log("[createVehicle] 🏢 Updating companyId for same user");
          transaction.update(vehicleRef, { 
            companyId: data.companyId,
            updatedAt: serverTimestamp(),
          });
          return { ...existingVehicle, companyId: data.companyId };
        }
        
        // Otherwise return existing vehicle as-is
        console.log("[createVehicle] ♻️ Returning existing vehicle:", existingVehicle.id);
        return existingVehicle;
      }
      
      // No existing vehicle - create new one with plate as ID (ATOMIC)
      console.log("[createVehicle] ➕ Creating NEW vehicle with plate as ID:", normalizedPlate);
      const vehicleData = {
        ...data,
        plate: normalizedPlate,
        createdAt: serverTimestamp(),
      };
      
      transaction.set(vehicleRef, vehicleData);
      console.log("[createVehicle] Document creation queued with ID:", normalizedPlate);
      
      return { id: normalizedPlate, ...vehicleData, createdAt: Date.now() } as Vehicle;
    });
    
    console.log("[createVehicle] Transaction completed successfully");
    return result;
  } catch (error) {
    console.error("[createVehicle] ERROR:", error);
    console.error("[createVehicle] Error details:", {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      code: (error as any)?.code,
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}

export async function getVehicle(vehicleId: string): Promise<Vehicle | null> {
  const vehicleRef = doc(db, COLLECTIONS.VEHICLES, vehicleId);
  const vehicleDoc = await getDoc(vehicleRef);
  
  if (!vehicleDoc.exists()) return null;
  return { id: vehicleDoc.id, ...convertTimestamps(vehicleDoc.data()) } as Vehicle;
}

export async function getUserVehicles(userId: string): Promise<Vehicle[]> {
  try {
    console.log("[getUserVehicles] Fetching vehicles for userId:", userId);
    
    const q = query(
      collection(db, COLLECTIONS.VEHICLES),
      where("userId", "==", userId)
    );
    
    console.log("[getUserVehicles] Executing query...");
    const snapshot = await getDocs(q);
    console.log("[getUserVehicles] Query completed. Documents found:", snapshot.size);
    
    const vehicles = snapshot.docs.map(doc => {
      const data = doc.data();
      console.log("[getUserVehicles] Processing doc:", doc.id, data);
      return {
        id: doc.id,
        ...convertTimestamps(data),
      };
    }) as Vehicle[];
    
    console.log("[getUserVehicles] Mapped vehicles:", vehicles);
    
    // Sort by createdAt in memory to avoid composite index requirement
    const sorted = vehicles.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    console.log("[getUserVehicles] Returning sorted vehicles:", sorted);
    
    return sorted;
  } catch (error) {
    console.error("[getUserVehicles] ERROR:", error);
    throw error;
  }
}

// NOTE: getVehicleByPlate and getVehicleByPlateOnly moved to PLATE-BASED VEHICLE/USER LOOKUP section above

export async function getCompanyVehicles(companyId: string): Promise<Vehicle[]> {
  const q = query(
    collection(db, COLLECTIONS.VEHICLES),
    where("companyId", "==", companyId)
  );
  
  const snapshot = await getDocs(q);
  const vehicles = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Vehicle[];
  
  return vehicles.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function updateVehicle(vehicleId: string, data: Partial<InsertVehicle>): Promise<void> {
  const vehicleRef = doc(db, COLLECTIONS.VEHICLES, vehicleId);
  await updateDoc(vehicleRef, data);
}

export async function deleteVehicle(vehicleId: string): Promise<void> {
  const vehicleRef = doc(db, COLLECTIONS.VEHICLES, vehicleId);
  await deleteDoc(vehicleRef);
}

export async function getAllVehicles(limitCount: number = 500): Promise<Vehicle[]> {
  try {
    console.log("🚗 [getAllVehicles] Starting query...");
    const q = query(
      collection(db, COLLECTIONS.VEHICLES),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    console.log("✅ [getAllVehicles] Query successful! Found", snapshot.size, "vehicles");
    
    const vehicles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Vehicle[];
    
    console.log("📋 [getAllVehicles] First 5 plates:", vehicles.slice(0, 5).map(v => v.plate));
    
    // Sort by plate for easier searching (handle null/undefined plates)
    return vehicles.sort((a, b) => (a.plate || "").localeCompare(b.plate || ""));
  } catch (error) {
    console.error("❌ [getAllVehicles] ERROR:", error);
    throw error;
  }
}

/**
 * Get vehicles with server-side pagination for large datasets
 * @param pageSize Number of items per page (default 50)
 * @param lastDoc Last document snapshot for cursor-based pagination
 * @returns Paginated vehicles with hasMore indicator
 */
export async function getVehiclesPaginated(pageSize: number = 50, lastDoc?: DocumentSnapshot): Promise<{
  vehicles: Vehicle[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const constraints: QueryConstraint[] = [
    orderBy("plate", "asc"),
    limit(pageSize + 1),
  ];
  
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }
  
  const q = query(collection(db, COLLECTIONS.VEHICLES), ...constraints);
  const snapshot = await getDocs(q);
  
  const hasMore = snapshot.docs.length > pageSize;
  const vehicles = snapshot.docs
    .slice(0, pageSize)
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Vehicle[];
  
  return {
    vehicles,
    lastDoc: snapshot.docs[pageSize - 1] || null,
    hasMore,
  };
}

// ============================================================================
// SERVICES
// ============================================================================

export async function createService(data: InsertService): Promise<Service> {
  const serviceData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.SERVICES), serviceData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Service;
}

export async function getService(serviceId: string): Promise<Service | null> {
  const serviceRef = doc(db, COLLECTIONS.SERVICES, serviceId);
  const serviceDoc = await getDoc(serviceRef);
  
  if (!serviceDoc.exists()) return null;
  return { id: serviceDoc.id, ...convertTimestamps(serviceDoc.data()) } as Service;
}

export async function getActiveServices(): Promise<Service[]> {
  const q = query(
    collection(db, COLLECTIONS.SERVICES),
    where("active", "==", true)
  );
  
  const snapshot = await getDocs(q);
  const services = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Service[];
  
  // Sort by createdAt in memory to avoid composite index requirement
  return services.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function getAllServices(): Promise<Service[]> {
  const q = query(
    collection(db, COLLECTIONS.SERVICES),
    orderBy("createdAt", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Service[];
}

export async function updateService(serviceId: string, data: Partial<InsertService>): Promise<void> {
  const serviceRef = doc(db, COLLECTIONS.SERVICES, serviceId);
  await updateDoc(serviceRef, data);
}

export async function deleteService(serviceId: string): Promise<void> {
  const serviceRef = doc(db, COLLECTIONS.SERVICES, serviceId);
  await deleteDoc(serviceRef);
}

// ============================================================================
// APPOINTMENTS
// ============================================================================

export async function createAppointment(data: InsertAppointment): Promise<Appointment> {
  const appointmentData = {
    ...data,
    reservationCode: generateReservationCode(),
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.APPOINTMENTS), appointmentData);
  const created = await getDoc(docRef);
  const appointment = { id: created.id, ...convertTimestamps(created.data()) } as Appointment;
  
  // Send notification to all employees/admins about new appointment
  try {
    const employees = await getEmployeeUsers();
    const notificationPromises = employees.map(employee => 
      createUserNotification(employee.id, {
        title: "Yeni Randevu",
        body: `${data.scheduledDate} ${data.scheduledTime} - Rezervasyon: ${appointmentData.reservationCode}`,
        type: "appointment",
        read: false,
        data: {
          appointmentId: appointment.id,
          link: `/employee/dashboard`,
        }
      })
    );
    await Promise.all(notificationPromises);
    console.log(`✅ Sent appointment notification to ${employees.length} employees`);
  } catch (error) {
    console.error("Error sending appointment notifications:", error);
    // Don't fail the appointment creation if notification fails
  }
  
  return appointment;
}

export async function getAppointment(appointmentId: string): Promise<Appointment | null> {
  const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
  const appointmentDoc = await getDoc(appointmentRef);
  
  if (!appointmentDoc.exists()) return null;
  return { id: appointmentDoc.id, ...convertTimestamps(appointmentDoc.data()) } as Appointment;
}

export async function getAppointmentByCode(reservationCode: string): Promise<Appointment | null> {
  const q = query(
    collection(db, COLLECTIONS.APPOINTMENTS),
    where("reservationCode", "==", reservationCode),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as Appointment;
}

export async function getUserAppointments(userId: string): Promise<Appointment[]> {
  const q = query(
    collection(db, COLLECTIONS.APPOINTMENTS),
    where("customerId", "==", userId)
  );
  
  const snapshot = await getDocs(q);
  const appointments = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Appointment[];
  
  // Sort by scheduledDate and scheduledTime in memory to avoid composite index requirement
  return appointments.sort((a, b) => {
    const dateCompare = (b.scheduledDate || "").localeCompare(a.scheduledDate || "");
    if (dateCompare !== 0) return dateCompare;
    return (b.scheduledTime || "").localeCompare(a.scheduledTime || "");
  });
}

export async function updateAppointment(appointmentId: string, data: Partial<InsertAppointment>): Promise<void> {
  const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
  await updateDoc(appointmentRef, data);
}

export async function cancelAppointment(appointmentId: string): Promise<void> {
  const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
  await updateDoc(appointmentRef, { status: "cancelled" });
}

export async function updateAppointmentPaymentStatus(appointmentId: string, paymentStatus: string): Promise<void> {
  const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
  await updateDoc(appointmentRef, { paymentStatus });
}

export async function getAppointmentsByDateRange(startDate: string, endDate: string): Promise<Appointment[]> {
  const q = query(
    collection(db, COLLECTIONS.APPOINTMENTS),
    where("scheduledDate", ">=", startDate),
    where("scheduledDate", "<=", endDate),
    orderBy("scheduledDate", "asc"),
    orderBy("scheduledTime", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Appointment[];
}

export async function getTodayPendingAppointments(): Promise<Appointment[]> {
  // Use local date format to match how appointments are created
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  
  // Query appointments for today
  const appointmentsQuery = query(
    collection(db, COLLECTIONS.APPOINTMENTS),
    where("scheduledDate", "==", todayStr)
  );
  
  // Query washes for today to exclude appointments that already have washes
  const washesQuery = query(
    collection(db, COLLECTIONS.WASHES),
    where("scheduledDate", "==", todayStr)
  );
  
  const [appointmentsSnapshot, washesSnapshot] = await Promise.all([
    getDocs(appointmentsQuery),
    getDocs(washesQuery),
  ]);
  
  const allAppointments = appointmentsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Appointment[];
  
  const allWashes = washesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
  
  // Get set of appointment IDs that already have washes
  const appointmentIdsWithWashes = new Set(
    allWashes.map(w => w.appointmentId).filter(Boolean)
  );
  
  // Filter for pending status, exclude those with washes, and sort by scheduledTime
  return allAppointments
    .filter(apt => 
      apt.status === "pending" && 
      !appointmentIdsWithWashes.has(apt.id)
    )
    .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
}

export async function getVehicleAppointments(vehicleId: string): Promise<Appointment[]> {
  const q = query(
    collection(db, COLLECTIONS.APPOINTMENTS),
    where("vehicleId", "==", vehicleId),
    orderBy("scheduledDate", "desc"),
    orderBy("scheduledTime", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Appointment[];
}

export async function deleteAppointment(appointmentId: string): Promise<void> {
  const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
  await deleteDoc(appointmentRef);
}

export async function getAllPendingAppointments(): Promise<Appointment[]> {
  try {
    console.log("🔍 [getAllPendingAppointments] Starting query...");
    
    // Get current month's date range (using local timezone, not UTC)
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format as YYYY-MM-DD using local timezone (not toISOString which uses UTC)
    const formatLocalDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const startDateStr = formatLocalDate(startOfMonth);
    const endDateStr = formatLocalDate(endOfMonth);
    
    console.log(`📅 [getAllPendingAppointments] Querying from ${startDateStr} to ${endDateStr}`);
    
    // Query for current month's pending appointments
    const q = query(
      collection(db, COLLECTIONS.APPOINTMENTS),
      where("status", "==", "pending"),
      where("scheduledDate", ">=", startDateStr),
      where("scheduledDate", "<=", endDateStr)
    );
    
    console.log("📡 [getAllPendingAppointments] Executing Firestore query...");
    const snapshot = await getDocs(q);
    console.log(`✅ [getAllPendingAppointments] Query successful! Found ${snapshot.size} appointments`);
    
    const appointments = snapshot.docs.map(doc => {
      const data = doc.data();
      console.log(`📋 [getAllPendingAppointments] Doc ${doc.id}:`, data);
      return {
        id: doc.id,
        ...convertTimestamps(data),
      };
    }) as Appointment[];
    
    // Client-side sorting by date then time
    const sorted = appointments.sort((a, b) => {
      const dateA = (a as any).scheduledDate || (a as any).appointmentDate || '';
      const dateB = (b as any).scheduledDate || (b as any).appointmentDate || '';
      const timeA = a.scheduledTime || '';
      const timeB = b.scheduledTime || '';
      
      const dateCompare = dateA.localeCompare(dateB);
      if (dateCompare !== 0) return dateCompare;
      return timeA.localeCompare(timeB);
    });
    
    console.log(`🎯 [getAllPendingAppointments] Returning ${sorted.length} sorted appointments for current month`);
    return sorted;
  } catch (error) {
    console.error("❌ [getAllPendingAppointments] ERROR:", error);
    throw error;
  }
}

export async function getAllConfirmedAppointments(): Promise<Appointment[]> {
  try {
    console.log("🔍 [getAllConfirmedAppointments] Starting query...");
    
    // Get current month's date range (using local timezone, not UTC)
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format as YYYY-MM-DD using local timezone (not toISOString which uses UTC)
    const formatLocalDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const startDateStr = formatLocalDate(startOfMonth);
    const endDateStr = formatLocalDate(endOfMonth);
    
    console.log(`📅 [getAllConfirmedAppointments] Querying from ${startDateStr} to ${endDateStr}`);
    
    // Query for current month's confirmed appointments
    const q = query(
      collection(db, COLLECTIONS.APPOINTMENTS),
      where("status", "==", "confirmed"),
      where("scheduledDate", ">=", startDateStr),
      where("scheduledDate", "<=", endDateStr)
    );
    
    console.log("📡 [getAllConfirmedAppointments] Executing Firestore query...");
    const snapshot = await getDocs(q);
    console.log(`✅ [getAllConfirmedAppointments] Query successful! Found ${snapshot.size} appointments`);
    
    const appointments = snapshot.docs.map(doc => {
      const data = doc.data();
      console.log(`📋 [getAllConfirmedAppointments] Doc ${doc.id}:`, data);
      return {
        id: doc.id,
        ...convertTimestamps(data),
      };
    }) as Appointment[];
    
    // Client-side sorting by date then time
    const sorted = appointments.sort((a, b) => {
      const dateA = (a as any).scheduledDate || (a as any).appointmentDate || '';
      const dateB = (b as any).scheduledDate || (b as any).appointmentDate || '';
      const timeA = a.scheduledTime || '';
      const timeB = b.scheduledTime || '';
      
      const dateCompare = dateA.localeCompare(dateB);
      if (dateCompare !== 0) return dateCompare;
      return timeA.localeCompare(timeB);
    });
    
    console.log(`🎯 [getAllConfirmedAppointments] Returning ${sorted.length} sorted appointments for current month`);
    return sorted;
  } catch (error) {
    console.error("❌ [getAllConfirmedAppointments] ERROR:", error);
    throw error;
  }
}

export async function getAllAppointments(limitCount: number = 500): Promise<Appointment[]> {
  try {
    console.log("🔍 [getAllAppointments] Starting query for all appointments...");
    
    const q = query(
      collection(db, COLLECTIONS.APPOINTMENTS),
      orderBy("scheduledDate", "desc"),
      limit(limitCount)
    );
    
    console.log("📡 [getAllAppointments] Executing Firestore query...");
    const snapshot = await getDocs(q);
    console.log(`✅ [getAllAppointments] Query successful! Found ${snapshot.size} appointments`);
    
    const appointments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Appointment[];
    
    console.log(`🎯 [getAllAppointments] Returning ${appointments.length} appointments`);
    return appointments;
  } catch (error) {
    console.error("❌ [getAllAppointments] ERROR:", error);
    throw error;
  }
}

/**
 * Get appointments with server-side pagination for large datasets
 * @param pageSize Number of items per page (default 50)
 * @param lastDoc Last document snapshot for cursor-based pagination
 * @returns Paginated appointments with hasMore indicator
 */
export async function getAppointmentsPaginated(pageSize: number = 50, lastDoc?: DocumentSnapshot): Promise<{
  appointments: Appointment[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const constraints: QueryConstraint[] = [
    orderBy("scheduledDate", "desc"),
    limit(pageSize + 1),
  ];
  
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }
  
  const q = query(collection(db, COLLECTIONS.APPOINTMENTS), ...constraints);
  const snapshot = await getDocs(q);
  
  const hasMore = snapshot.docs.length > pageSize;
  const appointments = snapshot.docs
    .slice(0, pageSize)
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Appointment[];
  
  return {
    appointments,
    lastDoc: snapshot.docs[pageSize - 1] || null,
    hasMore,
  };
}

// ============================================================================
// WASHES
// ============================================================================

export async function createWash(data: InsertWash): Promise<Wash> {
  const washData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.WASHES), washData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Wash;
}

// Transaction-based wash creation from appointment
export async function createWashFromAppointment(
  appointmentId: string,
  washData: Omit<InsertWash, 'appointmentId' | 'createdAt'>,
  employeeId: string
): Promise<Wash> {
  const { washId } = await createWashFromAppointmentTransaction(
    db,
    {
      APPOINTMENTS: COLLECTIONS.APPOINTMENTS,
      WASHES: COLLECTIONS.WASHES,
    },
    appointmentId,
    washData,
    employeeId
  );
  
  const wash = await getWash(washId);
  if (!wash) throw new Error("Failed to retrieve created wash");
  return wash;
}

export async function getWash(washId: string): Promise<Wash | null> {
  const washRef = doc(db, COLLECTIONS.WASHES, washId);
  const washDoc = await getDoc(washRef);
  
  if (!washDoc.exists()) return null;
  return { id: washDoc.id, ...convertTimestamps(washDoc.data()) } as Wash;
}

export async function getWashByAppointmentId(appointmentId: string): Promise<Wash | null> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("appointmentId", "==", appointmentId),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const washDoc = snapshot.docs[0];
  return { id: washDoc.id, ...convertTimestamps(washDoc.data()) } as Wash;
}

export async function getWashesByStatus(status: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("status", "==", status),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function getTodayWashes(): Promise<Wash[]> {
  // Use UTC date to match how washes are created (toISOString)
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]; // Match employee-dashboard format
  
  // REMOVED orderBy - it was causing query to fail (missing index or field)
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("scheduledDate", "==", todayStr)
  );
  
  const snapshot = await getDocs(q);
  
  // Sort by scheduledTime in memory instead
  const washes = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
  
  return washes.sort((a, b) => {
    const timeA = a.scheduledTime || "00:00";
    const timeB = b.scheduledTime || "00:00";
    return timeA.localeCompare(timeB);
  });
}

export async function getAllWashes(limitCount: number = 500): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

/**
 * Get washes with server-side pagination for large datasets
 * @param pageSize Number of items per page (default 50)
 * @param lastDoc Last document snapshot for cursor-based pagination
 * @returns Paginated washes with hasMore indicator
 */
export async function getWashesPaginated(pageSize: number = 50, lastDoc?: DocumentSnapshot): Promise<{
  washes: Wash[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const constraints: QueryConstraint[] = [
    orderBy("createdAt", "desc"),
    limit(pageSize + 1),
  ];
  
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }
  
  const q = query(collection(db, COLLECTIONS.WASHES), ...constraints);
  const snapshot = await getDocs(q);
  
  const hasMore = snapshot.docs.length > pageSize;
  const washes = snapshot.docs
    .slice(0, pageSize)
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Wash[];
  
  return {
    washes,
    lastDoc: snapshot.docs[pageSize - 1] || null,
    hasMore,
  };
}

export async function getWashesByDate(dateStr: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("scheduledDate", "==", dateStr),
    orderBy("scheduledTime", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function getEmployeeWashesByDate(employeeId: string, dateStr: string): Promise<Wash[]> {
  console.log("🔍 getEmployeeWashesByDate called with:", { employeeId, dateStr });
  
  try {
    const q = query(
      collection(db, COLLECTIONS.WASHES),
      where("employeeId", "==", employeeId),
      where("scheduledDate", "==", dateStr),
      orderBy("scheduledTime", "asc")
    );
    
    console.log("📡 Executing Firestore query...");
    const snapshot = await getDocs(q);
    console.log(`✅ Query successful! Found ${snapshot.docs.length} washes`);
    
    const washes = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Wash[];
    
    console.log("📋 Washes data:", washes);
    return washes;
  } catch (error: any) {
    console.error("❌ Error in getEmployeeWashesByDate:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    
    // Firebase index hatası kontrolü
    if (error.message?.includes("index")) {
      console.error("🔥 FIREBASE INDEX HATASI! Firebase Console'dan index oluşturmanız gerekiyor.");
      console.error("📝 Gerekli index: employeeId + scheduledDate + scheduledTime");
    }
    
    throw error;
  }
}

export async function getWashesByDateRange(startDate: string, endDate: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("scheduledDate", ">=", startDate),
    where("scheduledDate", "<=", endDate),
    orderBy("scheduledDate", "desc"),
    orderBy("scheduledTime", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function getUserWashes(userId: string): Promise<Wash[]> {
  console.log('[getUserWashes] Fetching washes for userId:', userId);
  
  try {
    // Use Cloud Function for secure access (bypasses Firestore rules)
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const getUserWashesSecure = httpsCallable(functions, 'getUserWashesSecure');
    
    console.log('[getUserWashes] Calling Cloud Function...');
    const result = await getUserWashesSecure({});
    const data = result.data as { success: boolean; washes: any[]; count: number };
    
    if (data.success && data.washes) {
      console.log('[getUserWashes] Cloud Function returned:', data.count, 'washes');
      return data.washes as Wash[];
    }
    
    console.warn('[getUserWashes] Cloud Function returned no washes');
    return [];
  } catch (cloudFunctionError: any) {
    console.warn('[getUserWashes] Cloud Function failed, falling back to direct query:', cloudFunctionError.message);
    
    // Fallback to direct Firestore query (will only work for customerId-based queries)
    try {
      const customerQuery = query(
        collection(db, COLLECTIONS.WASHES),
        where("customerId", "==", userId)
      );
      
      const customerSnapshot = await getDocs(customerQuery);
      const customerWashes = customerSnapshot.docs.map(doc => ({
        id: doc.id,
        ...convertTimestamps(doc.data()),
      })) as Wash[];
      
      console.log('[getUserWashes] Fallback - Washes by customerId:', customerWashes.length);
      return customerWashes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    } catch (error) {
      console.error('[getUserWashes] Fallback query also failed:', error);
      return [];
    }
  }
}

export async function getEmployeeWashes(employeeId: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("employeeId", "==", employeeId)
  );
  
  const snapshot = await getDocs(q);
  const washes = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
  
  // Sort by createdAt in memory to avoid composite index requirement
  return washes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function updateWash(washId: string, data: Partial<InsertWash>): Promise<void> {
  const washRef = doc(db, COLLECTIONS.WASHES, washId);
  await updateDoc(washRef, data);
}

// Transaction-based wash completion with loyalty and analytics updates
export async function completeWashWithLoyalty(
  washId: string,
  customerId: string | null,
  finalAmount: number
): Promise<{ 
  loyaltyUpdated: boolean; 
  pointsEarned: number; 
  analyticsUpdated: { customerUpdated: boolean; vehicleUpdated: boolean } 
}> {
  const loyaltyResult = await completeWashWithLoyaltyTransaction(
    db,
    {
      WASHES: COLLECTIONS.WASHES,
      LOYALTY: COLLECTIONS.LOYALTY,
    },
    washId,
    customerId,
    finalAmount
  );

  let analyticsUpdated = { customerUpdated: false, vehicleUpdated: false };

  try {
    const wash = await getWash(washId);
    if (wash) {
      analyticsUpdated = await updateAnalyticsAfterWashTransaction(
        db,
        {
          CUSTOMER_ANALYTICS: COLLECTIONS.CUSTOMER_ANALYTICS,
          VEHICLE_ANALYTICS: COLLECTIONS.VEHICLE_ANALYTICS,
          VEHICLES: COLLECTIONS.VEHICLES,
        },
        wash,
        finalAmount
      );
    }
  } catch (error) {
    console.error("Failed to update analytics after wash completion:", error);
  }

  return { ...loyaltyResult, analyticsUpdated };
}

export async function deleteWash(washId: string): Promise<void> {
  const washRef = doc(db, COLLECTIONS.WASHES, washId);
  await deleteDoc(washRef);
}

// ============================================================================
// EMPLOYEES
// ============================================================================

export async function createEmployee(data: InsertEmployeeProfile): Promise<EmployeeProfile> {
  const employeeData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.EMPLOYEES), employeeData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as EmployeeProfile;
}

export async function getActiveEmployees(): Promise<EmployeeProfile[]> {
  const q = query(
    collection(db, COLLECTIONS.EMPLOYEES),
    where("active", "==", true),
    orderBy("name", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as EmployeeProfile[];
}

export async function getAllEmployees(): Promise<EmployeeProfile[]> {
  const q = query(
    collection(db, COLLECTIONS.EMPLOYEES),
    orderBy("name", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as EmployeeProfile[];
}

export async function updateEmployee(employeeId: string, data: Partial<InsertEmployeeProfile>): Promise<void> {
  const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
  await updateDoc(employeeRef, data);
}

// ============================================================================
// COUPONS
// ============================================================================

export async function createCoupon(data: InsertCoupon): Promise<Coupon> {
  const couponData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.COUPONS), couponData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Coupon;
}

export async function getCouponByCode(code: string): Promise<Coupon | null> {
  const q = query(
    collection(db, COLLECTIONS.COUPONS),
    where("code", "==", code),
    where("active", "==", true),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as Coupon;
}

/**
 * SORUN #3 FIX: Validate coupon and check if it's active and not expired
 * @param code - Coupon code to validate
 * @returns Object with isValid flag and coupon data or error message
 */
export async function validateCoupon(code: string, customerId?: string | null): Promise<{
  isValid: boolean;
  coupon?: Coupon;
  error?: string;
  userUsageCount?: number;
}> {
  if (!code || code.trim() === "") {
    return { isValid: false, error: "Kupon kodu boş olamaz." };
  }
  
  const coupon = await getCouponByCode(code.trim().toUpperCase());
  
  if (!coupon) {
    return { isValid: false, error: "Geçersiz kupon kodu." };
  }
  
  if (!coupon.active) {
    return { isValid: false, error: "Bu kupon artık aktif değil." };
  }
  
  const now = Date.now();
  
  // Check if coupon is not yet valid
  if (coupon.validFrom && coupon.validFrom > now) {
    return { isValid: false, error: "Bu kupon henuz gecerli degil." };
  }
  
  // Check expiry date
  if (coupon.validUntil && coupon.validUntil < now) {
    return { isValid: false, error: "Bu kuponun suresi dolmus." };
  }
  
  // Check total usage limit if exists
  if (coupon.maxUses && coupon.currentUses && coupon.currentUses >= coupon.maxUses) {
    return { isValid: false, error: "Bu kuponun kullanım limiti dolmuş." };
  }
  
  // Check per-user usage limit if exists and customerId is provided
  if (coupon.usageLimitPerUser && customerId) {
    const userUsageCount = await getUserCouponUsageCount(coupon.id, customerId);
    if (userUsageCount >= coupon.usageLimitPerUser) {
      return { 
        isValid: false, 
        error: `Bu kuponu daha önce ${userUsageCount} kez kullandınız. Maksimum ${coupon.usageLimitPerUser} kez kullanabilirsiniz.`,
        userUsageCount,
      };
    }
    return { isValid: true, coupon, userUsageCount };
  }
  
  return { isValid: true, coupon };
}

export async function getUserCouponUsageCount(couponId: string, customerId: string): Promise<number> {
  const q = query(
    collection(db, COLLECTIONS.COUPON_USAGE_LOG),
    where("couponId", "==", couponId),
    where("customerId", "==", customerId)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.length;
}

export async function getAllCoupons(): Promise<Coupon[]> {
  const q = query(
    collection(db, COLLECTIONS.COUPONS),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Coupon[];
}

export async function updateCoupon(couponId: string, data: Partial<InsertCoupon>): Promise<void> {
  const couponRef = doc(db, COLLECTIONS.COUPONS, couponId);
  await updateDoc(couponRef, data);
}

// ============================================================================
// COUPON USAGE LOG
// ============================================================================

export async function createCouponUsageLog(data: InsertCouponUsageLog): Promise<CouponUsageLog> {
  const docRef = await addDoc(collection(db, COLLECTIONS.COUPON_USAGE_LOG), data);
  return { id: docRef.id, ...data } as CouponUsageLog;
}

export async function getCouponUsageLogs(couponId: string): Promise<CouponUsageLog[]> {
  const q = query(
    collection(db, COLLECTIONS.COUPON_USAGE_LOG),
    where("couponId", "==", couponId),
    orderBy("usedAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CouponUsageLog[];
}

export async function incrementCouponUsage(couponId: string): Promise<void> {
  const couponRef = doc(db, COLLECTIONS.COUPONS, couponId);
  await updateDoc(couponRef, {
    currentUses: increment(1),
  });
}

// ============================================================================
// EXPENSES
// ============================================================================

export async function createExpense(data: InsertExpense): Promise<Expense> {
  const expenseData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.EXPENSES), expenseData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Expense;
}

export async function getExpensesByDateRange(startDate: string, endDate: string): Promise<Expense[]> {
  const q = query(
    collection(db, COLLECTIONS.EXPENSES),
    where("date", ">=", startDate),
    where("date", "<=", endDate),
    orderBy("date", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Expense[];
}

export async function getAllExpenses(): Promise<Expense[]> {
  // Limit to last 500 expenses for performance
  const q = query(
    collection(db, COLLECTIONS.EXPENSES),
    orderBy("date", "desc"),
    limit(500)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Expense[];
}

export async function deleteExpense(expenseId: string): Promise<void> {
  const expenseRef = doc(db, COLLECTIONS.EXPENSES, expenseId);
  await deleteDoc(expenseRef);
}

// ============================================================================
// FIXED EXPENSES (Sabit Giderler)
// ============================================================================

export async function createFixedExpense(data: InsertFixedExpense): Promise<FixedExpense> {
  const fixedExpenseData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.FIXED_EXPENSES), fixedExpenseData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as FixedExpense;
}

export async function getAllFixedExpenses(): Promise<FixedExpense[]> {
  const q = query(
    collection(db, COLLECTIONS.FIXED_EXPENSES),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as FixedExpense[];
}

export async function deleteFixedExpense(fixedExpenseId: string): Promise<void> {
  const fixedExpenseRef = doc(db, COLLECTIONS.FIXED_EXPENSES, fixedExpenseId);
  await deleteDoc(fixedExpenseRef);
}

// ============================================================================
// REVIEWS
// ============================================================================

export async function createReview(data: InsertReview): Promise<Review> {
  const reviewData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.REVIEWS), reviewData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Review;
}

export async function getPublicReviews(): Promise<Review[]> {
  const q = query(
    collection(db, COLLECTIONS.REVIEWS),
    where("approved", "==", true),
    where("public", "==", true),
    orderBy("createdAt", "desc"),
    limit(50)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Review[];
}

export async function getPendingReviews(): Promise<Review[]> {
  const q = query(
    collection(db, COLLECTIONS.REVIEWS),
    where("approved", "==", false),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Review[];
}

export async function updateReview(reviewId: string, data: Partial<InsertReview>): Promise<void> {
  const reviewRef = doc(db, COLLECTIONS.REVIEWS, reviewId);
  await updateDoc(reviewRef, data);
}

// ============================================================================
// PHOTO CLEANUP (KVKK Compliance)
// ============================================================================

/**
 * Delete photos older than 7 days for KVKK compliance
 * Returns number of photos deleted
 */
export async function cleanupOldPhotos(): Promise<{ deletedPhotos: number; updatedWashes: number }> {
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  const sevenDaysAgoTimestamp = Timestamp.fromMillis(sevenDaysAgo);
  
  // Find completed washes older than 7 days with photos
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("status", "==", "completed"),
    where("completedAt", "<=", sevenDaysAgoTimestamp)
  );
  
  const snapshot = await getDocs(q);
  let deletedPhotos = 0;
  let updatedWashes = 0;
  
  for (const washDoc of snapshot.docs) {
    const wash = washDoc.data();
    const photos = wash.photos || [];
    
    if (photos.length === 0) continue;
    
    // Delete photos from Firebase Storage
    for (const photo of photos) {
      try {
        // Extract storage path from URL
        // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?...
        const url = photo.url;
        const pathMatch = url.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const storagePath = decodeURIComponent(pathMatch[1]);
          const photoRef = ref(storage, storagePath);
          await deleteObject(photoRef);
          deletedPhotos++;
        }
      } catch (error) {
        console.error(`Error deleting photo ${photo.url}:`, error);
        // Continue with other photos even if one fails
      }
    }
    
    // Update wash document to remove photos array
    await updateDoc(doc(db, COLLECTIONS.WASHES, washDoc.id), {
      photos: [],
    });
    updatedWashes++;
  }
  
  return { deletedPhotos, updatedWashes };
}

// ============================================================================
// CORPORATE COMPANIES
// ============================================================================

export async function createCorporateCompany(data: InsertCorporateCompany): Promise<CorporateCompany> {
  const companyData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_COMPANIES), companyData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as CorporateCompany;
}

export async function getAllCorporateCompanies(): Promise<CorporateCompany[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_COMPANIES),
    orderBy("companyName", "asc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateCompany[];
}

export async function getCorporateCompanyById(companyId: string): Promise<CorporateCompany | null> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_COMPANIES, companyId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return { id: snapshot.id, ...convertTimestamps(snapshot.data()) } as CorporateCompany;
}

export async function updateCorporateCompany(companyId: string, data: Partial<CorporateCompany>): Promise<void> {
  const companyRef = doc(db, COLLECTIONS.CORPORATE_COMPANIES, companyId);
  await updateDoc(companyRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCorporateCompany(companyId: string): Promise<void> {
  const companyRef = doc(db, COLLECTIONS.CORPORATE_COMPANIES, companyId);
  await deleteDoc(companyRef);
}

// ============================================================================
// CORPORATE PAYMENTS (CARİ ÖDEME)
// ============================================================================

export async function createCorporatePayment(data: InsertCorporatePayment): Promise<CorporatePayment> {
  const paymentData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_PAYMENTS), paymentData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as CorporatePayment;
}

export async function getCorporatePayments(companyId?: string): Promise<CorporatePayment[]> {
  let q;
  
  if (companyId) {
    q = query(
      collection(db, COLLECTIONS.CORPORATE_PAYMENTS),
      where("companyId", "==", companyId),
      orderBy("paymentDate", "desc"),
      limit(100)
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.CORPORATE_PAYMENTS),
      orderBy("paymentDate", "desc"),
      limit(100)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporatePayment[];
}

export async function deleteCorporatePayment(paymentId: string): Promise<void> {
  const paymentRef = doc(db, COLLECTIONS.CORPORATE_PAYMENTS, paymentId);
  await deleteDoc(paymentRef);
}

// ============================================================================
// CORPORATE DEBTS (CARİ BORÇ)
// ============================================================================

export async function createCorporateDebt(data: InsertCorporateDebt): Promise<CorporateDebt> {
  const debtData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_DEBTS), debtData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as CorporateDebt;
}

export async function getCorporateDebts(companyId?: string): Promise<CorporateDebt[]> {
  let q;
  
  if (companyId) {
    q = query(
      collection(db, COLLECTIONS.CORPORATE_DEBTS),
      where("companyId", "==", companyId),
      orderBy("washDate", "desc"),
      limit(500)
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.CORPORATE_DEBTS),
      orderBy("washDate", "desc"),
      limit(500)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateDebt[];
}

export async function getUnpaidCorporateDebts(): Promise<CorporateDebt[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_DEBTS),
    where("isPaid", "==", false),
    orderBy("washDate", "desc"),
    limit(500)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateDebt[];
}

export async function getCorporateBalanceSummary(): Promise<{
  companyId: string;
  companyName: string;
  totalDebt: number;
  unpaidDebtCount: number;
}[]> {
  const debts = await getUnpaidCorporateDebts();
  
  const summaryMap = new Map<string, {
    companyId: string;
    companyName: string;
    totalDebt: number;
    unpaidDebtCount: number;
  }>();
  
  for (const debt of debts) {
    const existing = summaryMap.get(debt.companyId) || {
      companyId: debt.companyId,
      companyName: debt.companyName,
      totalDebt: 0,
      unpaidDebtCount: 0,
    };
    
    existing.totalDebt += debt.amount;
    existing.unpaidDebtCount += 1;
    summaryMap.set(debt.companyId, existing);
  }
  
  return Array.from(summaryMap.values()).sort((a, b) => b.totalDebt - a.totalDebt);
}

export async function getCompanyNetBalance(companyId: string): Promise<{
  totalDebts: number;
  totalPaid: number;
  netBalance: number;
}> {
  const debtsQuery = query(
    collection(db, COLLECTIONS.CORPORATE_DEBTS),
    where("companyId", "==", companyId)
  );
  const debtsSnapshot = await getDocs(debtsQuery);
  
  const paymentsQuery = query(
    collection(db, COLLECTIONS.CORPORATE_PAYMENTS),
    where("companyId", "==", companyId)
  );
  const paymentsSnapshot = await getDocs(paymentsQuery);
  
  const totalDebts = debtsSnapshot.docs.reduce((sum, doc) => sum + (doc.data().amount || 0), 0);
  const totalPaid = paymentsSnapshot.docs.reduce((sum, doc) => sum + (doc.data().amount || 0), 0);
  
  return {
    totalDebts,
    totalPaid,
    netBalance: totalDebts - totalPaid,
  };
}

export async function markDebtsAsPaid(debtIds: string[], paymentId: string): Promise<void> {
  const batch = writeBatch(db);
  const now = Date.now();
  
  for (const debtId of debtIds) {
    const debtRef = doc(db, COLLECTIONS.CORPORATE_DEBTS, debtId);
    batch.update(debtRef, {
      isPaid: true,
      paidAt: now,
      paymentId: paymentId,
    });
  }
  
  await batch.commit();
}

// ============================================================================
// CORPORATE QUOTES
// ============================================================================

async function generateQuoteNumber(): Promise<string> {
  const year = new Date().getFullYear();
  const quotesRef = collection(db, COLLECTIONS.CORPORATE_QUOTES);
  
  const q = query(
    quotesRef,
    where("quoteNumber", ">=", `TKL-${year}-`),
    orderBy("quoteNumber", "desc"),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return `TKL-${year}-001`;
  }
  
  const lastQuote = snapshot.docs[0].data();
  const lastNumber = parseInt(lastQuote.quoteNumber.split("-")[2]) || 0;
  const nextNumber = (lastNumber + 1).toString().padStart(3, "0");
  
  return `TKL-${year}-${nextNumber}`;
}

export async function createCorporateQuote(data: InsertCorporateQuote): Promise<CorporateQuote> {
  const quoteData = {
    ...data,
    status: "pending",
    quotedAmount: null,
    quotedNotes: null,
    statusHistory: [{
      status: "pending",
      timestamp: Date.now(),
    }],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_QUOTES), quoteData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as CorporateQuote;
}

export async function getAllCorporateQuotes(): Promise<CorporateQuote[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_QUOTES),
    orderBy("createdAt", "desc"),
    limit(100)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateQuote[];
}

export async function getCorporateQuoteById(quoteId: string): Promise<CorporateQuote | null> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_QUOTES, quoteId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return { id: snapshot.id, ...convertTimestamps(snapshot.data()) } as CorporateQuote;
}

export async function updateCorporateQuote(quoteId: string, data: Partial<CorporateQuote>): Promise<void> {
  const quoteRef = doc(db, COLLECTIONS.CORPORATE_QUOTES, quoteId);
  await updateDoc(quoteRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCorporateQuote(quoteId: string): Promise<void> {
  const quoteRef = doc(db, COLLECTIONS.CORPORATE_QUOTES, quoteId);
  await deleteDoc(quoteRef);
}

export async function updateQuoteStatus(
  quoteId: string, 
  newStatus: string, 
  updatedBy?: string
): Promise<void> {
  const quoteRef = doc(db, COLLECTIONS.CORPORATE_QUOTES, quoteId);
  const quoteSnap = await getDoc(quoteRef);
  
  if (!quoteSnap.exists()) {
    throw new Error("Quote not found");
  }
  
  const currentQuote = quoteSnap.data() as CorporateQuote;
  const existingHistory = currentQuote.statusHistory || [];
  
  const cleanedHistory = existingHistory
    .filter((entry: any) => entry && typeof entry === 'object')
    .map((entry: any) => {
      const clean: { status: string; timestamp: number; updatedBy?: string } = {
        status: entry.status || '',
        timestamp: entry.timestamp || Date.now(),
      };
      if (entry.updatedBy) {
        clean.updatedBy = entry.updatedBy;
      }
      return clean;
    });
  
  const newEntry: { status: string; timestamp: number; updatedBy?: string } = {
    status: newStatus,
    timestamp: Date.now(),
  };
  if (updatedBy) {
    newEntry.updatedBy = updatedBy;
  }
  
  cleanedHistory.push(newEntry);
  
  const updateData: Record<string, unknown> = {
    status: newStatus,
    statusHistory: cleanedHistory,
    updatedAt: serverTimestamp(),
  };
  
  if (updatedBy) {
    updateData.updatedBy = updatedBy;
  }
  
  await updateDoc(quoteRef, updateData);
}

export async function finalizeQuoteWithDetails(
  quoteId: string,
  updatedBy: string
): Promise<string> {
  const quoteNumber = await generateQuoteNumber();
  
  await updateDoc(doc(db, COLLECTIONS.CORPORATE_QUOTES, quoteId), {
    quoteNumber,
    status: "quoted",
    updatedAt: serverTimestamp(),
    updatedBy,
  });
  
  await updateQuoteStatus(quoteId, "quoted", updatedBy);
  
  return quoteNumber;
}

// ============================================================================
// REVIEWS
// ============================================================================

export async function getAllReviews(): Promise<Review[]> {
  const q = query(
    collection(db, COLLECTIONS.REVIEWS),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Review[];
}

export async function approveReview(reviewId: string): Promise<void> {
  const reviewRef = doc(db, COLLECTIONS.REVIEWS, reviewId);
  await updateDoc(reviewRef, { 
    approved: true,
    public: true 
  });
}

export async function deleteReview(reviewId: string): Promise<void> {
  const reviewRef = doc(db, COLLECTIONS.REVIEWS, reviewId);
  await deleteDoc(reviewRef);
}

// ============================================================================
// PAYMENTS
// ============================================================================

export async function createPayment(data: InsertPayment): Promise<Payment> {
  const paymentData = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.PAYMENTS), paymentData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Payment;
}

export async function getAllPayments(): Promise<Payment[]> {
  // Limit to last 500 payments for performance
  const q = query(
    collection(db, COLLECTIONS.PAYMENTS),
    orderBy("createdAt", "desc"),
    limit(500)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Payment[];
}

export async function getPaymentsByDateRange(startDate: number, endDate: number): Promise<Payment[]> {
  // Get all payments first, then filter by scheduledDate (string format)
  const q = query(
    collection(db, COLLECTIONS.PAYMENTS),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  const allPayments = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Payment[];
  
  // Filter by scheduledDate (YYYY-MM-DD format)
  const startDateStr = new Date(startDate).toISOString().split('T')[0];
  const endDateStr = new Date(endDate).toISOString().split('T')[0];
  
  return allPayments.filter(payment => {
    return payment.scheduledDate >= startDateStr && payment.scheduledDate <= endDateStr;
  });
}

export async function deletePayment(paymentId: string): Promise<void> {
  const paymentRef = doc(db, COLLECTIONS.PAYMENTS, paymentId);
  await deleteDoc(paymentRef);
}

export async function getDailyStats(dateStr: string) {
  const paymentsQuery = query(
    collection(db, COLLECTIONS.PAYMENTS),
    where("scheduledDate", "==", dateStr),
    orderBy("createdAt", "desc")
  );
  
  const [washes, paymentsSnapshot] = await Promise.all([
    getWashesByDate(dateStr),
    getDocs(paymentsQuery)
  ]);
  
  const payments = paymentsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Payment[];
  
  const completedWashes = washes.filter(w => w.status === 'completed');
  const totalRevenue = payments.reduce((sum, p) => sum + p.amount, 0);
  const cashRevenue = payments.filter(p => p.method === 'cash').reduce((sum, p) => sum + p.amount, 0);
  const cardRevenue = payments.filter(p => p.method === 'card').reduce((sum, p) => sum + p.amount, 0);
  const paidWashIds = new Set(payments.map(p => p.washId).filter(Boolean));
  const unpaidWashes = completedWashes.filter(w => !paidWashIds.has(w.id));
  
  return {
    totalWashes: washes.length,
    completedWashes: completedWashes.length,
    paidWashes: paidWashIds.size,
    unpaidWashes: unpaidWashes.length,
    totalRevenue,
    cashRevenue,
    cardRevenue,
    unpaidWashesList: unpaidWashes,
  };
}

// ============================================================================
// CUSTOMER LOYALTY
// ============================================================================

export async function getCustomerLoyalty(customerId: string): Promise<CustomerLoyalty | null> {
  const q = query(
    collection(db, COLLECTIONS.LOYALTY),
    where("customerId", "==", customerId),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  
  // If no loyalty record exists, try to sync from washes
  if (snapshot.empty) {
    console.log(`[getCustomerLoyalty] No loyalty record for ${customerId}, attempting sync...`);
    try {
      const syncResult = await syncLoyaltyFromWashes();
      if (syncResult.success && syncResult.totalWashes > 0) {
        // Re-query after sync
        const retrySnapshot = await getDocs(q);
        if (!retrySnapshot.empty) {
          const doc = retrySnapshot.docs[0];
          return { id: doc.id, ...convertTimestamps(doc.data()) } as CustomerLoyalty;
        }
      }
    } catch (error) {
      console.warn('[getCustomerLoyalty] Sync failed:', error);
    }
    return null;
  }
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as CustomerLoyalty;
}

export async function syncLoyaltyFromWashes(): Promise<{ success: boolean; message: string; totalWashes: number; points: number }> {
  try {
    console.log('[syncLoyaltyFromWashes] Calling Cloud Function...');
    const syncFn = httpsCallable(functions, 'syncLoyaltyFromWashes');
    const result = await syncFn({});
    const data = result.data as { success: boolean; message: string; totalWashes: number; points: number };
    console.log('[syncLoyaltyFromWashes] Sync result:', data);
    return data;
  } catch (error: any) {
    console.error('[syncLoyaltyFromWashes] Error:', error);
    return { success: false, message: error.message, totalWashes: 0, points: 0 };
  }
}

export async function createCustomerLoyalty(data: InsertCustomerLoyalty): Promise<CustomerLoyalty> {
  const loyaltyData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.LOYALTY), loyaltyData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as CustomerLoyalty;
}

export async function updateCustomerLoyalty(loyaltyId: string, data: Partial<InsertCustomerLoyalty>): Promise<void> {
  const loyaltyRef = doc(db, COLLECTIONS.LOYALTY, loyaltyId);
  await updateDoc(loyaltyRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCustomerLoyalty(loyaltyId: string): Promise<void> {
  const loyaltyRef = doc(db, COLLECTIONS.LOYALTY, loyaltyId);
  await deleteDoc(loyaltyRef);
}

// Add points after a wash completion (called by employee/cashier after completing wash)
export async function addLoyaltyPoints(customerId: string, amount: number): Promise<void> {
  const loyaltyQuery = query(
    collection(db, COLLECTIONS.LOYALTY),
    where("customerId", "==", customerId),
    limit(1)
  );
  
  const snapshot = await getDocs(loyaltyQuery);
  
  // Create loyalty record if it doesn't exist
  if (snapshot.empty) {
    await createCustomerLoyalty({
      customerId,
      points: LOYALTY_CONFIG.POINTS_PER_WASH,
      totalSpent: amount,
      totalWashes: 1,
      lastVisit: Date.now(),
    });
    return;
  }
  
  // Use Firestore transaction to prevent race conditions on concurrent updates
  const loyaltyRef = doc(db, COLLECTIONS.LOYALTY, snapshot.docs[0].id);
  
  await runTransaction(db, async (transaction) => {
    const loyaltyDoc = await transaction.get(loyaltyRef);
    
    if (!loyaltyDoc.exists()) {
      throw new Error("Loyalty document no longer exists!");
    }
    
    const loyalty = { id: loyaltyDoc.id, ...convertTimestamps(loyaltyDoc.data()) } as CustomerLoyalty;
    
    const newTotalSpent = loyalty.totalSpent + amount;
    const newTotalWashes = loyalty.totalWashes + 1;
    const newPoints = loyalty.points + LOYALTY_CONFIG.POINTS_PER_WASH;
    const newTier = calculateTier(newTotalSpent);
    
    transaction.update(loyaltyRef, {
      points: newPoints,
      tier: newTier,
      totalSpent: newTotalSpent,
      totalWashes: newTotalWashes,
      lastVisit: Date.now(),
      updatedAt: serverTimestamp(),
    });
  });
}

// Redeem points for free wash (uses transaction to prevent race conditions)
export async function redeemLoyaltyPoints(
  customerId: string, 
  pointsToRedeem: number,
  redeemedBy: string
): Promise<boolean> {
  const loyaltyQuery = query(
    collection(db, COLLECTIONS.LOYALTY),
    where("customerId", "==", customerId),
    limit(1)
  );
  
  const snapshot = await getDocs(loyaltyQuery);
  
  if (snapshot.empty) {
    return false;
  }
  
  const loyaltyRef = doc(db, COLLECTIONS.LOYALTY, snapshot.docs[0].id);
  const loyaltyId = snapshot.docs[0].id;
  
  try {
    // Use transaction to prevent race conditions
    await runTransaction(db, async (transaction) => {
      const loyaltyDoc = await transaction.get(loyaltyRef);
      
      if (!loyaltyDoc.exists()) {
        throw new Error("Loyalty document no longer exists!");
      }
      
      const loyalty = { id: loyaltyDoc.id, ...convertTimestamps(loyaltyDoc.data()) } as CustomerLoyalty;
      
      if (loyalty.points < pointsToRedeem) {
        throw new Error("Insufficient points");
      }
      
      // Deduct points atomically
      transaction.update(loyaltyRef, {
        points: loyalty.points - pointsToRedeem,
        updatedAt: serverTimestamp(),
      });
    });
    
    // Create redemption record after successful transaction
    const redemptionValue = (pointsToRedeem / LOYALTY_CONFIG.FREE_WASH_POINTS) * LOYALTY_CONFIG.FREE_WASH_VALUE;
    await createLoyaltyRedemption({
      customerId,
      loyaltyId,
      pointsRedeemed: pointsToRedeem,
      redemptionValue,
      redeemedBy,
    });
    
    return true;
  } catch (error) {
    console.error("Error redeeming points:", error);
    return false;
  }
}

// Get all loyalty records (for admin)
export async function getAllLoyaltyRecords(): Promise<CustomerLoyalty[]> {
  const q = query(
    collection(db, COLLECTIONS.LOYALTY),
    orderBy("totalSpent", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerLoyalty[];
}

// Initialize loyalty records for all customers who don't have one
export async function initializeLoyaltyForAllCustomers(): Promise<{ 
  success: boolean; 
  initialized: number; 
  skipped: number; 
  error?: string 
}> {
  try {
    // Get all users
    const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    const allUsers = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get all existing loyalty records
    const loyaltySnapshot = await getDocs(collection(db, COLLECTIONS.LOYALTY));
    const existingLoyaltyCustomerIds = new Set(
      loyaltySnapshot.docs.map(doc => doc.data().customerId)
    );
    
    // Filter users who don't have loyalty records
    const usersWithoutLoyalty = allUsers.filter(
      user => !existingLoyaltyCustomerIds.has(user.id)
    );
    
    // Create loyalty records for users without one
    let initialized = 0;
    for (const user of usersWithoutLoyalty) {
      await createCustomerLoyalty({
        customerId: user.id,
        points: 0, // Start with 0 points
        totalSpent: 0,
        totalWashes: 0,
        lastVisit: Date.now(),
      });
      initialized++;
    }
    
    return {
      success: true,
      initialized,
      skipped: allUsers.length - initialized,
    };
  } catch (error) {
    console.error("Error initializing loyalty for customers:", error);
    return {
      success: false,
      initialized: 0,
      skipped: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// ============================================================================
// LOYALTY REDEMPTION OPERATIONS
// ============================================================================

// Create loyalty redemption record
export async function createLoyaltyRedemption(data: InsertLoyaltyRedemption): Promise<LoyaltyRedemption> {
  const docRef = await addDoc(collection(db, COLLECTIONS.LOYALTY_REDEMPTIONS), {
    ...data,
    createdAt: serverTimestamp(),
  });
  
  const docSnap = await getDoc(docRef);
  return {
    id: docRef.id,
    ...convertTimestamps(docSnap.data()),
  } as LoyaltyRedemption;
}

// Get all redemptions (for admin)
export async function getAllRedemptions(): Promise<LoyaltyRedemption[]> {
  const q = query(
    collection(db, COLLECTIONS.LOYALTY_REDEMPTIONS),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as LoyaltyRedemption[];
}

// Get redemptions by customer
export async function getRedemptionsByCustomer(customerId: string): Promise<LoyaltyRedemption[]> {
  const q = query(
    collection(db, COLLECTIONS.LOYALTY_REDEMPTIONS),
    where("customerId", "==", customerId),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as LoyaltyRedemption[];
}

// ============================================================================
// REWARD COUPON OPERATIONS (QR Kodlu Hediye Kuponları)
// ============================================================================

// Generate unique coupon code
function generateCouponCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'TF-';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Create reward coupon
export async function createRewardCoupon(data: InsertRewardCoupon): Promise<RewardCoupon> {
  const couponData = {
    ...data,
    code: data.code || generateCouponCode(),
    status: 'active',
    usedAt: null,
    usedBy: null,
    washId: null,
    createdAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, COLLECTIONS.REWARD_COUPONS), couponData);
  const docSnap = await getDoc(docRef);
  return {
    id: docRef.id,
    ...convertTimestamps(docSnap.data()),
  } as RewardCoupon;
}

// Get customer's reward coupons
export async function getCustomerRewardCoupons(customerId: string): Promise<RewardCoupon[]> {
  const q = query(
    collection(db, COLLECTIONS.REWARD_COUPONS),
    where("customerId", "==", customerId),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as RewardCoupon[];
}

// Get active reward coupons for customer
export async function getActiveCustomerCoupons(customerId: string): Promise<RewardCoupon[]> {
  const q = query(
    collection(db, COLLECTIONS.REWARD_COUPONS),
    where("customerId", "==", customerId),
    where("status", "==", "active")
  );
  
  const snapshot = await getDocs(q);
  const now = Date.now();
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    }) as RewardCoupon)
    .filter(coupon => coupon.expiresAt > now);
}

// Get coupon by code (for cashier scanning)
export async function getRewardCouponByCode(code: string): Promise<RewardCoupon | null> {
  const q = query(
    collection(db, COLLECTIONS.REWARD_COUPONS),
    where("code", "==", code),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as RewardCoupon;
}

// Use/redeem reward coupon (by cashier)
export async function redeemRewardCoupon(
  couponId: string, 
  cashierId: string, 
  washId?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const couponRef = doc(db, COLLECTIONS.REWARD_COUPONS, couponId);
    const couponSnap = await getDoc(couponRef);
    
    if (!couponSnap.exists()) {
      return { success: false, message: 'Kupon bulunamadı' };
    }
    
    const coupon = { id: couponSnap.id, ...convertTimestamps(couponSnap.data()) } as RewardCoupon;
    
    if (coupon.status === 'used') {
      return { success: false, message: 'Bu kupon zaten kullanılmış' };
    }
    
    if (coupon.status === 'expired' || coupon.expiresAt < Date.now()) {
      return { success: false, message: 'Bu kuponun süresi dolmuş' };
    }
    
    await updateDoc(couponRef, {
      status: 'used',
      usedAt: serverTimestamp(),
      usedBy: cashierId,
      washId: washId || null,
    });
    
    return { success: true, message: 'Kupon başarıyla kullanıldı' };
  } catch (error) {
    console.error('Error redeeming coupon:', error);
    return { success: false, message: 'Kupon kullanılırken hata oluştu' };
  }
}

// Get all reward coupons (for admin)
export async function getAllRewardCoupons(): Promise<RewardCoupon[]> {
  const q = query(
    collection(db, COLLECTIONS.REWARD_COUPONS),
    orderBy("createdAt", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as RewardCoupon[];
}

// ============================================================================
// NOTIFICATION TEMPLATE SEED (One-time setup)
// ============================================================================

export async function seedNotificationTemplates(): Promise<{ success: boolean; count: number; error?: string }> {
  try {
    // Check if templates already exist
    const existingTemplates = await getDocs(collection(db, COLLECTIONS.NOTIFICATION_TEMPLATES));
    if (!existingTemplates.empty) {
      return { success: false, count: 0, error: "Templates already exist" };
    }

    const templates = [
      // APPOINTMENT TEMPLATES
      {
        type: "APPOINTMENT_CREATED",
        title: "🔔 Yeni Randevu Talebi",
        body: "Rezervasyon: {{reservationCode}}\nTarih: {{scheduledDate}} {{scheduledTime}}\nMüşteri: {{customerName}}",
        targetRole: "employee",
        enabled: true,
      },
      {
        type: "APPOINTMENT_CONFIRMED",
        title: "✅ Randevunuz Onaylandı!",
        body: "Rezervasyon: {{reservationCode}}\nTarih: {{scheduledDate}} {{scheduledTime}}\nAracınızı bekliyoruz!",
        targetRole: "customer",
        enabled: true,
      },
      {
        type: "APPOINTMENT_CANCELLED",
        title: "❌ Randevunuz İptal Edildi",
        body: "Rezervasyon: {{reservationCode}} iptal edildi.\n{{cancelReason}}",
        targetRole: "customer",
        enabled: true,
      },
      {
        type: "APPOINTMENT_RESCHEDULED",
        title: "🔄 Randevunuz Değiştirildi",
        body: "Rezervasyon: {{reservationCode}}\nYeni tarih: {{scheduledDate}} {{scheduledTime}}",
        targetRole: "customer",
        enabled: true,
      },
      // WASH STATUS TEMPLATES
      {
        type: "WASH_QUEUED",
        title: "📋 Aracınız Sıraya Alındı",
        body: "{{vehiclePlate}} plakalı aracınız yıkama sırasına alındı.\nRezervasyon: {{reservationCode}}",
        targetRole: "customer",
        enabled: true,
      },
      {
        type: "WASH_STARTED",
        title: "🧼 Yıkama Başladı",
        body: "{{vehiclePlate}} plakalı aracınızın yıkaması başladı.\nTahmini süre: {{estimatedDuration}} dakika",
        targetRole: "customer",
        enabled: true,
      },
      {
        type: "WASH_COMPLETED",
        title: "🎉 Aracınız Hazır!",
        body: "{{vehiclePlate}} plakalı aracınız hazır!\nRezervasyon: {{reservationCode}}\nToplam: {{totalAmount}} TL",
        targetRole: "customer",
        enabled: true,
      },
      // PAYMENT TEMPLATES
      {
        type: "PAYMENT_RECEIVED",
        title: "💰 Ödeme Alındı",
        body: "Rezervasyon: {{reservationCode}}\nTutar: {{amount}} TL\nYöntem: {{paymentMethod}}",
        targetRole: "customer",
        enabled: true,
      },
    ];

    // Add all templates
    const promises = templates.map(template => 
      addDoc(collection(db, COLLECTIONS.NOTIFICATION_TEMPLATES), {
        ...template,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      })
    );

    await Promise.all(promises);
    
    return { success: true, count: templates.length };
  } catch (error) {
    console.error("Error seeding notification templates:", error);
    return { success: false, count: 0, error: (error as Error).message };
  }
}

// ============================================================================
// SITE SETTINGS
// ============================================================================

export async function getSiteSettings(): Promise<SiteSettings | null> {
  try {
    // Settings her zaman "default" ID'sine sahip tek bir kayıt
    const docRef = doc(db, COLLECTIONS.SITE_SETTINGS, "default");
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as SiteSettings;
    }
    
    // İlk kez çalışıyorsa default değerlerle oluştur
    const defaultSettings: InsertSiteSettings = {
      businessName: "TemizFilo",
      phone: "+90 555 123 45 67",
      email: "info@temizfilo.com",
      whatsapp: "+905551234567",
      address: "Örnek Mahalle, Araç Yıkama Cad. No:123, Kadıköy / İstanbul",
      googleMapsUrl: "",
      showBusinessHours: true,
      updatedBy: "system",
    };
    
    await setDoc(docRef, {
      ...defaultSettings,
      updatedAt: Date.now(),
    });
    
    const newDocSnap = await getDoc(docRef);
    return { id: newDocSnap.id, ...newDocSnap.data() } as SiteSettings;
  } catch (error) {
    console.error("Error getting site settings:", error);
    return null;
  }
}

export async function updateSiteSettings(data: InsertSiteSettings): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.SITE_SETTINGS, "default");
    await setDoc(docRef, {
      ...data,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (error) {
    console.error("Error updating site settings:", error);
    throw error;
  }
}

// ============================================================================
// SYSTEM SETTINGS (Loyalty, Business Hours, KVKK)
// ============================================================================

export async function getSystemSettings(): Promise<SystemSettings | null> {
  try {
    const docRef = doc(db, COLLECTIONS.SYSTEM_SETTINGS, "global");
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as SystemSettings;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting system settings:", error);
    return null;
  }
}

export async function initializeSystemSettings(adminId: string): Promise<SystemSettings> {
  const docRef = doc(db, COLLECTIONS.SYSTEM_SETTINGS, "global");
  
  const defaultSettings: InsertSystemSettings = {
    loyalty: {
      pointsPerWash: 1,
      freeWashPoints: 5,
      freeWashValue: 50,
      tierThresholds: {
        bronze: 0,
        silver: 1000,
        gold: 5000,
      },
      tierDiscounts: {
        bronze: 0,
        silver: 5,
        gold: 10,
      },
    },
    operations: {
      businessHours: {
        open: "08:00",
        close: "19:00",
        earliestAppointment: "09:00",
      },
      capacity: {
        maxConcurrentCars: 3,
        slotDurationMinutes: 60,
      },
    },
    compliance: {
      photoRetentionDays: 7,
      dataRetentionDays: 90,
    },
    updatedBy: adminId,
  };
  
  await setDoc(docRef, {
    ...defaultSettings,
    updatedAt: Date.now(),
  });
  
  const newDocSnap = await getDoc(docRef);
  return { id: newDocSnap.id, ...newDocSnap.data() } as SystemSettings;
}

export async function updateSystemSettings(data: Partial<InsertSystemSettings>, adminId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.SYSTEM_SETTINGS, "global");
    await setDoc(docRef, {
      ...data,
      updatedBy: adminId,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (error) {
    console.error("Error updating system settings:", error);
    throw error;
  }
}

// ============================================================================
// NOTIFICATION TEMPLATES
// ============================================================================

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  const q = query(collection(db, COLLECTIONS.NOTIFICATION_TEMPLATES), orderBy("type", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as NotificationTemplate[];
}

export async function getNotificationTemplateByType(type: string): Promise<NotificationTemplate | null> {
  try {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATION_TEMPLATES),
      where("type", "==", type),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return { id: doc.id, ...convertTimestamps(doc.data()) } as NotificationTemplate;
  } catch (error) {
    console.error("Error getting notification template by type:", error);
    return null;
  }
}

export async function updateNotificationTemplate(
  id: string, 
  data: Partial<InsertNotificationTemplate>, 
  adminId?: string
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.NOTIFICATION_TEMPLATES, id);
    
    // Get current template for version history
    const currentDoc = await getDoc(docRef);
    if (currentDoc.exists()) {
      const current = currentDoc.data() as NotificationTemplate;
      const newVersion = (current.version || 1) + 1;
      
      // Save version history before updating
      const versionData: InsertTemplateVersion = {
        templateId: id,
        type: current.type,
        version: current.version || 1,
        title: current.title,
        body: current.body,
        targetRole: current.targetRole,
        enabled: current.enabled,
        updatedBy: adminId || "unknown",
      };
      
      await addDoc(collection(db, COLLECTIONS.TEMPLATE_VERSIONS), {
        ...versionData,
        createdAt: Date.now(),
      });
      
      // Update template with new version
      await updateDoc(docRef, {
        ...data,
        version: newVersion,
        updatedBy: adminId,
        updatedAt: Date.now(),
      });
    }
  } catch (error) {
    console.error("Error updating notification template:", error);
    throw error;
  }
}

export async function getTemplateVersionHistory(templateId: string): Promise<TemplateVersion[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.TEMPLATE_VERSIONS),
      where("templateId", "==", templateId),
      orderBy("version", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as TemplateVersion[];
  } catch (error) {
    console.error("Error getting template version history:", error);
    return [];
  }
}

export async function createNotificationTemplate(data: InsertNotificationTemplate): Promise<NotificationTemplate> {
  const templateData = {
    ...data,
    version: 1,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.NOTIFICATION_TEMPLATES), templateData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as NotificationTemplate;
}

export async function deleteNotificationTemplate(id: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.NOTIFICATION_TEMPLATES, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting notification template:", error);
    throw error;
  }
}

// ============================================================================
// NOTIFICATION SOUNDS (Bildirim Sesleri)
// ============================================================================

export async function getNotificationSounds(): Promise<NotificationSoundType[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATION_SOUNDS),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as NotificationSoundType[];
  } catch (error) {
    console.error("Error getting notification sounds:", error);
    return [];
  }
}

export async function getActiveNotificationSounds(): Promise<NotificationSoundType[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATION_SOUNDS),
      where("isActive", "==", true),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as NotificationSoundType[];
  } catch (error) {
    console.error("Error getting active notification sounds:", error);
    return [];
  }
}

export async function getDefaultNotificationSound(): Promise<NotificationSoundType | null> {
  try {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATION_SOUNDS),
      where("isDefault", "==", true),
      where("isActive", "==", true),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...convertTimestamps(doc.data()) } as NotificationSoundType;
  } catch (error) {
    console.error("Error getting default notification sound:", error);
    return null;
  }
}

export async function createNotificationSound(data: InsertNotificationSound): Promise<NotificationSoundType> {
  try {
    const now = Date.now();
    
    if (data.isDefault) {
      const existingDefault = await getDefaultNotificationSound();
      if (existingDefault) {
        await updateDoc(doc(db, COLLECTIONS.NOTIFICATION_SOUNDS, existingDefault.id), { isDefault: false });
      }
    }
    
    const soundData = {
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.NOTIFICATION_SOUNDS), soundData);
    const created = await getDoc(docRef);
    return { id: created.id, ...convertTimestamps(created.data()) } as NotificationSoundType;
  } catch (error) {
    console.error("Error creating notification sound:", error);
    throw error;
  }
}

export async function updateNotificationSound(id: string, data: Partial<InsertNotificationSound>): Promise<void> {
  try {
    if (data.isDefault) {
      const existingDefault = await getDefaultNotificationSound();
      if (existingDefault && existingDefault.id !== id) {
        await updateDoc(doc(db, COLLECTIONS.NOTIFICATION_SOUNDS, existingDefault.id), { isDefault: false });
      }
    }
    
    const docRef = doc(db, COLLECTIONS.NOTIFICATION_SOUNDS, id);
    await updateDoc(docRef, { ...data, updatedAt: Date.now() });
  } catch (error) {
    console.error("Error updating notification sound:", error);
    throw error;
  }
}

export async function deleteNotificationSound(id: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.NOTIFICATION_SOUNDS, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting notification sound:", error);
    throw error;
  }
}

// ============================================================================
// GIFTS (Hediyeler)
// ============================================================================

export async function getGifts(): Promise<Gift[]> {
  const q = query(collection(db, COLLECTIONS.GIFTS), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Gift[];
}

export async function getActiveGifts(): Promise<Gift[]> {
  const q = query(
    collection(db, COLLECTIONS.GIFTS), 
    where("active", "==", true),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Gift[];
}

export async function createGift(data: InsertGift): Promise<Gift> {
  const giftData = {
    ...data,
    createdAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.GIFTS), giftData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as Gift;
}

export async function updateGift(id: string, data: Partial<InsertGift>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GIFTS, id);
  await updateDoc(docRef, data);
}

export async function deleteGift(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GIFTS, id);
  await deleteDoc(docRef);
}

// ============================================================================
// FEEDBACK (Şikayet ve Öneriler)
// ============================================================================

export async function getAllFeedback(): Promise<any[]> {
  const q = query(collection(db, COLLECTIONS.FEEDBACK), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function createFeedback(data: any): Promise<any> {
  const feedbackData = {
    ...data,
    status: "new",
    createdAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.FEEDBACK), feedbackData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) };
}

export async function updateFeedback(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FEEDBACK, id);
  const updateData: any = { ...data };
  
  // If marking as resolved, set resolvedAt timestamp
  if (data.status === "resolved" && !data.resolvedAt) {
    updateData.resolvedAt = Date.now();
  }
  
  await updateDoc(docRef, updateData);
}

export async function deleteFeedback(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FEEDBACK, id);
  await deleteDoc(docRef);
}

// ============================================================================
// SMS INTEGRATION
// ============================================================================

// SMS Templates
export async function getSmsTemplates(): Promise<any[]> {
  const q = query(collection(db, COLLECTIONS.SMS_TEMPLATES), orderBy("type", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function getSmsTemplateByType(type: string): Promise<any | null> {
  const q = query(
    collection(db, COLLECTIONS.SMS_TEMPLATES),
    where("type", "==", type),
    where("enabled", "==", true)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) };
}

export async function createSmsTemplate(data: any): Promise<any> {
  const templateData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.SMS_TEMPLATES), templateData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) };
}

export async function updateSmsTemplate(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.SMS_TEMPLATES, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteSmsTemplate(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.SMS_TEMPLATES, id);
  await deleteDoc(docRef);
}

// SMS Logs
export async function getSmsLogs(limitCount: number = 100): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.SMS_LOGS),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function getSmsLogsByUserId(userId: string): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.SMS_LOGS),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

// SMS Settings
export async function getSmsSettings(): Promise<any | null> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.SMS_SETTINGS));
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) };
}

export async function updateSmsSettings(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.SMS_SETTINGS, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function createSmsSettings(data: any): Promise<any> {
  const docRef = await addDoc(collection(db, COLLECTIONS.SMS_SETTINGS), {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) };
}

// Default SMS Templates - Varsayılan şablonlar (Cloud Functions ile uyumlu)
// NOT: Frontend "message" alanını, Cloud Functions "text" alanını kullanıyor
// Her iki alanı da kaydediyoruz
const DEFAULT_SMS_TEMPLATES = [
  {
    type: "welcome",
    name: "Hos Geldiniz",
    message: `Merhaba {{name}}! TemizFilo ailesine hos geldiniz! Aracinizi en iyi sekilde temizlemek icin buradayiz. Randevu: temizfilo.web.app/book - TemizFilo`,
    text: `Merhaba {{name}}! TemizFilo ailesine hos geldiniz! Aracinizi en iyi sekilde temizlemek icin buradayiz. Randevu: temizfilo.web.app/book - TemizFilo`,
    variables: ["name"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "verification_code",
    name: "Dogrulama Kodu",
    message: `TemizFilo dogrulama kodunuz: {{code}} - Bu kodu kimseyle paylasmayiniz. TemizFilo`,
    text: `TemizFilo dogrulama kodunuz: {{code}} - Bu kodu kimseyle paylasmayiniz. TemizFilo`,
    variables: ["code"],
    enabled: true,
    iysRequired: false,
  },
  {
    type: "appointment_created",
    name: "Randevu Olusturuldu",
    message: `Randevunuz olusturuldu! {{date}} - {{time}} | Kod: {{code}} | {{plate}} | {{services}} | {{amount}} TL | Takip: temizfilo.web.app/track?code={{code}} - TemizFilo`,
    text: `Randevunuz olusturuldu! {{date}} - {{time}} | Kod: {{code}} | {{plate}} | {{services}} | {{amount}} TL | Takip: temizfilo.web.app/track?code={{code}} - TemizFilo`,
    variables: ["date", "time", "code", "plate", "services", "amount"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "appointment_confirmed",
    name: "Randevu Onaylandi",
    message: `Randevunuz onaylandi! {{date}} - {{time}} | Kod: {{code}} | {{plate}} | Sizi bekliyoruz! TemizFilo`,
    text: `Randevunuz onaylandi! {{date}} - {{time}} | Kod: {{code}} | {{plate}} | Sizi bekliyoruz! TemizFilo`,
    variables: ["date", "time", "code", "plate"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "APPOINTMENT_REMINDER",
    name: "Randevu Hatirlatma",
    message: `Hatirlatma: Yarin randevunuz var! {{date}} - {{time}} | Kod: {{code}} | Sizi bekliyoruz! TemizFilo`,
    text: `Hatirlatma: Yarin randevunuz var! {{date}} - {{time}} | Kod: {{code}} | Sizi bekliyoruz! TemizFilo`,
    variables: ["date", "time", "code"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "wash_started",
    name: "Yikama Basladi",
    message: `Yikama basladi! {{plate}} plakali aracinizin yikamasi basladi. Tahmini sure: {{duration}} dk | Takip: temizfilo.web.app/track?code={{code}} - TemizFilo`,
    text: `Yikama basladi! {{plate}} plakali aracinizin yikamasi basladi. Tahmini sure: {{duration}} dk | Takip: temizfilo.web.app/track?code={{code}} - TemizFilo`,
    variables: ["plate", "duration", "code"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "wash_ready",
    name: "Arac Hazir",
    message: `Araciniz hazir! {{plate}} plakali araciniz teslim alinmayi bekliyor. Kod: {{code}} | Toplam: {{amount}} TL - TemizFilo`,
    text: `Araciniz hazir! {{plate}} plakali araciniz teslim alinmayi bekliyor. Kod: {{code}} | Toplam: {{amount}} TL - TemizFilo`,
    variables: ["plate", "code", "amount"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "loyalty_points",
    name: "Sadakat Puani",
    message: `Tebrikler! {{points}} puan kazandiniz! Toplam: {{totalPoints}} puan | Seviye: {{tier}} - TemizFilo`,
    text: `Tebrikler! {{points}} puan kazandiniz! Toplam: {{totalPoints}} puan | Seviye: {{tier}} - TemizFilo`,
    variables: ["points", "totalPoints", "tier"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "loyalty_gift",
    name: "Sadakat Hediyesi",
    message: `Sevgili {{customerName}}, size bir hediyemiz var: {{giftName}}! {{giftDescription}} - TemizFilo`,
    text: `Sevgili {{customerName}}, size bir hediyemiz var: {{giftName}}! {{giftDescription}} - TemizFilo`,
    variables: ["customerName", "giftName", "giftDescription"],
    enabled: true,
    iysRequired: true,
  },
  {
    type: "campaign",
    name: "Kampanya",
    message: "{{message}}",
    text: "{{message}}",
    variables: ["message"],
    enabled: true,
    iysRequired: true,
  },
];

// Seed default SMS templates - update existing or create new
export async function seedDefaultSmsTemplates(): Promise<{ created: number; updated: number; existing: number }> {
  const existingTemplates = await getSmsTemplates();
  
  console.log("Checking SMS templates...");
  let created = 0;
  let updated = 0;
  
  for (const defaultTemplate of DEFAULT_SMS_TEMPLATES) {
    try {
      // Check if template with this type already exists
      const existing = existingTemplates.find(t => t.type === defaultTemplate.type);
      
      if (existing) {
        // Update if message is empty or missing
        if (!existing.message || existing.message.trim() === '') {
          await updateSmsTemplate(existing.id, {
            message: defaultTemplate.message,
            text: defaultTemplate.text,
            name: defaultTemplate.name,
            variables: defaultTemplate.variables,
            enabled: defaultTemplate.enabled,
            iysRequired: defaultTemplate.iysRequired,
          });
          updated++;
          console.log(`Updated SMS template: ${defaultTemplate.type}`);
        }
      } else {
        // Create new template
        await createSmsTemplate(defaultTemplate);
        created++;
        console.log(`Created SMS template: ${defaultTemplate.type}`);
      }
    } catch (error) {
      console.error(`Error processing SMS template ${defaultTemplate.type}:`, error);
    }
  }
  
  console.log(`SMS templates: ${created} created, ${updated} updated.`);
  return { created, updated, existing: existingTemplates.length - updated };
}

// ============================================================================
// CAMPAIGN SMS TEMPLATES
// ============================================================================

export async function getCampaignSmsTemplates(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function getCampaignSmsTemplate(id: string): Promise<any | null> {
  const docRef = doc(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) };
}

export async function createCampaignSmsTemplate(data: any): Promise<any> {
  const docRef = await addDoc(collection(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES), {
    ...data,
    usageCount: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return { id: docRef.id, ...data };
}

export async function updateCampaignSmsTemplate(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteCampaignSmsTemplate(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES, id);
  await deleteDoc(docRef);
}

export async function incrementCampaignTemplateUsage(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CAMPAIGN_SMS_TEMPLATES, id);
  await updateDoc(docRef, {
    usageCount: increment(1),
    lastUsedAt: Date.now(),
    updatedAt: Date.now(),
  });
}

// ============================================================================
// EMPLOYEE PAYMENTS
// ============================================================================

export async function createEmployeePayment(data: InsertEmployeePayment): Promise<EmployeePayment> {
  const paymentData = {
    ...data,
    createdAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.EMPLOYEE_PAYMENTS), paymentData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as EmployeePayment;
}

export async function getEmployeePayments(employeeId?: string): Promise<EmployeePayment[]> {
  let q;
  if (employeeId) {
    q = query(
      collection(db, COLLECTIONS.EMPLOYEE_PAYMENTS),
      where("employeeId", "==", employeeId),
      orderBy("paymentDate", "desc")
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.EMPLOYEE_PAYMENTS),
      orderBy("paymentDate", "desc")
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as EmployeePayment[];
}

export async function getEmployeePaymentsByMonth(month: string): Promise<EmployeePayment[]> {
  const q = query(
    collection(db, COLLECTIONS.EMPLOYEE_PAYMENTS),
    where("month", "==", month),
    orderBy("paymentDate", "desc")
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as EmployeePayment[];
}

export async function updateEmployeePayment(paymentId: string, data: Partial<InsertEmployeePayment>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.EMPLOYEE_PAYMENTS, paymentId);
  await updateDoc(docRef, data);
}

export async function deleteEmployeePayment(paymentId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.EMPLOYEE_PAYMENTS, paymentId);
  await deleteDoc(docRef);
}

// ============================================================================
// INVENTORY MANAGEMENT
// ============================================================================

export async function createInventoryItem(data: InsertInventoryItem): Promise<InventoryItem> {
  const itemData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.INVENTORY), itemData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as InventoryItem;
}

export async function getInventoryItems(): Promise<InventoryItem[]> {
  const q = query(
    collection(db, COLLECTIONS.INVENTORY),
    orderBy("name", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as InventoryItem[];
}

export async function getInventoryItem(itemId: string): Promise<InventoryItem | null> {
  const docRef = doc(db, COLLECTIONS.INVENTORY, itemId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...convertTimestamps(snapshot.data()) } as InventoryItem;
}

export async function updateInventoryItem(itemId: string, data: Partial<InsertInventoryItem>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.INVENTORY, itemId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteInventoryItem(itemId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.INVENTORY, itemId);
  await deleteDoc(docRef);
}

// Kritik stok seviyesinin altındaki ürünleri getir
export async function getCriticalStockItems(): Promise<InventoryItem[]> {
  const allItems = await getInventoryItems();
  return allItems.filter(item => item.currentStock <= item.criticalLevel);
}

// ============================================================================
// STOCK MOVEMENTS
// ============================================================================

export async function createStockMovement(data: InsertStockMovement): Promise<StockMovement> {
  const movementData = {
    ...data,
    createdAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.STOCK_MOVEMENTS), movementData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) } as StockMovement;
}

export async function getStockMovements(itemId?: string): Promise<StockMovement[]> {
  let q;
  if (itemId) {
    q = query(
      collection(db, COLLECTIONS.STOCK_MOVEMENTS),
      where("itemId", "==", itemId),
      orderBy("createdAt", "desc"),
      limit(50)
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.STOCK_MOVEMENTS),
      orderBy("createdAt", "desc"),
      limit(100)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as StockMovement[];
}

// Stok güncelleme + stock movement kaydı (transaction ile)
export async function updateInventoryStock(
  itemId: string,
  quantity: number, // + giriş, - çıkış
  type: "in" | "out" | "adjustment",
  reason: string,
  performedBy: string,
  relatedEntityId?: string,
  notes?: string
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const itemRef = doc(db, COLLECTIONS.INVENTORY, itemId);
    const itemDoc = await transaction.get(itemRef);
    
    if (!itemDoc.exists()) {
      throw new Error("Inventory item not found");
    }
    
    const item = itemDoc.data() as InventoryItem;
    const newStock = item.currentStock + quantity;
    
    if (newStock < 0) {
      throw new Error("Stock cannot be negative");
    }
    
    // Update inventory item
    transaction.update(itemRef, {
      currentStock: newStock,
      updatedAt: Date.now(),
      lastRestockDate: type === "in" ? Date.now() : item.lastRestockDate,
    });
    
    // Create stock movement record
    const movementRef = doc(collection(db, COLLECTIONS.STOCK_MOVEMENTS));
    transaction.set(movementRef, {
      itemId,
      type,
      quantity,
      reason,
      performedBy,
      relatedEntityId,
      notes,
      createdAt: Date.now(),
    });
  });
}

// ============================================================================
// BRAND SETTINGS (Web Builder)
// ============================================================================

export async function getBrandSettings(): Promise<any | null> {
  try {
    const docRef = doc(db, COLLECTIONS.BRAND_SETTINGS, "default");
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...convertTimestamps(docSnap.data()) };
    }
    
    return null;
  } catch (error) {
    console.error("Error getting brand settings:", error);
    return null;
  }
}

export async function updateBrandSettings(data: any, adminId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.BRAND_SETTINGS, "default");
    await setDoc(docRef, {
      ...data,
      updatedAt: Date.now(),
      updatedBy: adminId,
    }, { merge: true });
  } catch (error) {
    console.error("Error updating brand settings:", error);
    throw error;
  }
}

export async function initializeBrandSettings(adminId: string): Promise<any> {
  const docRef = doc(db, COLLECTIONS.BRAND_SETTINGS, "default");
  
  const defaultSettings = {
    logoText: "TemizFilo",
    colors: {
      primary: "210 100% 56%",
      secondary: "199 89% 80%",
      accent: "199 89% 94%",
      destructive: "356 90% 54%",
      background: "0 0% 100%",
      foreground: "0 0% 10%",
      muted: "0 0% 95%",
    },
    hero: {
      title: "Aracınız İçin Mükemmel Bakım",
      subtitle: "Profesyonel ekibimiz ve modern ekipmanlarımızla aracınıza hak ettiği bakımı sunuyoruz",
      badge: "Premium Araç Yıkama Hizmeti",
      cta1Text: "Hemen Randevu Al",
      cta1Link: "/book",
      cta2Text: "Randevu Takip",
      cta2Link: "/track",
      backgroundType: "gradient",
    },
    stats: [
      { value: "10,000+", label: "Mutlu Müşteri", icon: "users" },
      { value: "4.9/5", label: "Ortalama Puan", icon: "star" },
      { value: "15+", label: "Yıllık Tecrübe", icon: "award" },
      { value: "24/7", label: "Müşteri Desteği", icon: "clock" },
    ],
    footer: {
      description: "Profesyonel araç yıkama ve bakım hizmetleri",
      copyrightText: "Tüm hakları saklıdır",
    },
    updatedBy: adminId,
    updatedAt: Date.now(),
  };
  
  await setDoc(docRef, defaultSettings);
  
  const newDocSnap = await getDoc(docRef);
  return { id: newDocSnap.id, ...convertTimestamps(newDocSnap.data()) };
}

// ============================================================================
// FAQS
// ============================================================================

export async function createFaq(data: any): Promise<any> {
  const faqData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.FAQS), faqData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) };
}

export async function getFaqs(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.FAQS),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  // Filter out explicitly inactive items (active === false)
  // Include items with active === true OR active === undefined
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    }))
    .filter(item => item.active !== false);
}

export async function getAllFaqs(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.FAQS),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function updateFaq(faqId: string, data: any): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FAQS, faqId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteFaq(faqId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FAQS, faqId);
  await deleteDoc(docRef);
}

// ============================================================================
// FEATURES
// ============================================================================

export async function createFeature(data: any): Promise<any> {
  const featureData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.FEATURES), featureData);
  const created = await getDoc(docRef);
  return { id: created.id, ...convertTimestamps(created.data()) };
}

export async function getFeatures(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.FEATURES),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  // Filter out explicitly inactive items (active === false)
  // Include items with active === true OR active === undefined
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    }))
    .filter(item => item.active !== false);
}

export async function getAllFeatures(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.FEATURES),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function updateFeature(featureId: string, data: any): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FEATURES, featureId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteFeature(featureId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.FEATURES, featureId);
  await deleteDoc(docRef);
}

// ============================================================================
// DUPLICATE CLEANUP UTILITIES
// ============================================================================

export async function cleanupDuplicateFaqs(): Promise<{ deleted: number }> {
  const q = query(collection(db, COLLECTIONS.FAQS), orderBy("order", "asc"));
  const snapshot = await getDocs(q);
  
  const seenQuestions = new Map<string, string>();
  const toDelete: string[] = [];
  
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const question = data.question;
    
    if (seenQuestions.has(question)) {
      // Duplicate found
      toDelete.push(docSnap.id);
    } else {
      // First time seeing this question
      seenQuestions.set(question, docSnap.id);
    }
  });
  
  // Delete duplicates
  for (const id of toDelete) {
    await deleteDoc(doc(db, COLLECTIONS.FAQS, id));
  }
  
  return { deleted: toDelete.length };
}

export async function cleanupDuplicateFeatures(): Promise<{ deleted: number }> {
  const q = query(collection(db, COLLECTIONS.FEATURES), orderBy("order", "asc"));
  const snapshot = await getDocs(q);
  
  const seenTitles = new Map<string, string>();
  const toDelete: string[] = [];
  
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const title = data.title;
    
    if (seenTitles.has(title)) {
      // Duplicate found
      toDelete.push(docSnap.id);
    } else {
      // First time seeing this title
      seenTitles.set(title, docSnap.id);
    }
  });
  
  // Delete duplicates
  for (const id of toDelete) {
    await deleteDoc(doc(db, COLLECTIONS.FEATURES, id));
  }
  
  return { deleted: toDelete.length };
}

// ============================================================================
// HOME PAGE SECTIONS
// ============================================================================

export async function getHomePageSections(): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.HOME_PAGE_SECTIONS),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function updateHomePageSection(sectionId: string, data: any, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.HOME_PAGE_SECTIONS, sectionId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function initializeHomePageSections(adminId: string): Promise<void> {
  const defaultSections = [
    { id: "hero", type: "hero", enabled: true, order: 1 },
    { id: "stats", type: "stats", enabled: true, order: 2 },
    { id: "features", type: "features", enabled: true, order: 3 },
    { id: "services", type: "services", enabled: true, order: 4 },
    { id: "subscriptions", type: "subscriptions", enabled: true, order: 5 },
    { id: "testimonials", type: "testimonials", enabled: true, order: 6 },
    { id: "faq", type: "faq", enabled: true, order: 7 },
    { id: "contact", type: "contact", enabled: true, order: 8 },
    { id: "cta", type: "cta", enabled: true, order: 9 },
  ];
  
  for (const section of defaultSections) {
    const docRef = doc(db, COLLECTIONS.HOME_PAGE_SECTIONS, section.id);
    await setDoc(docRef, {
      ...section,
      updatedAt: Date.now(),
      updatedBy: adminId,
    });
  }
}

export async function ensureMissingHomePageSections(adminId: string): Promise<{ added: string[] }> {
  const defaultSections = [
    { id: "hero", type: "hero", enabled: true, order: 1 },
    { id: "stats", type: "stats", enabled: true, order: 2 },
    { id: "features", type: "features", enabled: true, order: 3 },
    { id: "services", type: "services", enabled: true, order: 4 },
    { id: "subscriptions", type: "subscriptions", enabled: true, order: 5 },
    { id: "testimonials", type: "testimonials", enabled: true, order: 6 },
    { id: "faq", type: "faq", enabled: true, order: 7 },
    { id: "contact", type: "contact", enabled: true, order: 8 },
    { id: "cta", type: "cta", enabled: true, order: 9 },
  ];
  
  const added: string[] = [];
  
  for (const section of defaultSections) {
    const docRef = doc(db, COLLECTIONS.HOME_PAGE_SECTIONS, section.id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      await setDoc(docRef, {
        ...section,
        updatedAt: Date.now(),
        updatedBy: adminId,
      });
      added.push(section.id);
    }
  }
  
  return { added };
}

// ============================================================================
// TESTIMONIALS (Müşteri Yorumları)
// ============================================================================

export async function createTestimonial(data: InsertTestimonial, adminId: string): Promise<Testimonial> {
  const testimonialRef = doc(collection(db, COLLECTIONS.TESTIMONIALS));
  const testimonialData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    updatedBy: adminId,
  };
  await setDoc(testimonialRef, testimonialData);
  
  return { id: testimonialRef.id, ...testimonialData } as Testimonial;
}

export async function getTestimonials(): Promise<Testimonial[]> {
  const q = query(
    collection(db, COLLECTIONS.TESTIMONIALS),
    where("active", "==", true),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Testimonial[];
}

export async function getAllTestimonials(): Promise<Testimonial[]> {
  const q = query(
    collection(db, COLLECTIONS.TESTIMONIALS),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Testimonial[];
}

export async function getFeaturedTestimonials(): Promise<Testimonial[]> {
  const q = query(
    collection(db, COLLECTIONS.TESTIMONIALS),
    where("active", "==", true),
    where("featured", "==", true),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Testimonial[];
}

export async function updateTestimonial(testimonialId: string, data: Partial<InsertTestimonial>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.TESTIMONIALS, testimonialId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function deleteTestimonial(testimonialId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.TESTIMONIALS, testimonialId);
  await deleteDoc(docRef);
}

// ============================================================================
// CONTACT SETTINGS
// ============================================================================

export async function getContactSettings(): Promise<ContactSettings | null> {
  const docRef = doc(db, COLLECTIONS.CONTACT_SETTINGS, "main");
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as ContactSettings;
}

export async function updateContactSettings(data: Partial<InsertContactSettings>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CONTACT_SETTINGS, "main");
  await setDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  }, { merge: true });
}

// ============================================================================
// GALLERY (Before/After Photos)
// ============================================================================

export async function createGalleryItem(data: InsertGalleryItem, adminId: string): Promise<GalleryItem> {
  const galleryRef = doc(collection(db, COLLECTIONS.GALLERY));
  
  // Get current count to set order
  const snapshot = await getDocs(collection(db, COLLECTIONS.GALLERY));
  const order = snapshot.size;
  
  const galleryData = {
    ...data,
    order: data.order !== undefined ? data.order : order,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    updatedBy: adminId,
  };
  await setDoc(galleryRef, galleryData);
  
  return { id: galleryRef.id, ...galleryData } as GalleryItem;
}

export async function getGalleryItems(): Promise<GalleryItem[]> {
  try {
    // Try with order first
    const q = query(
      collection(db, COLLECTIONS.GALLERY),
      where("active", "==", true),
      orderBy("order", "asc")
    );
    const snapshot = await getDocs(q);
    console.log("✅ Gallery query with order successful, items:", snapshot.size);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as GalleryItem[];
  } catch (error: any) {
    console.warn("⚠️ Gallery query with order failed, trying without order:", error.message);
    // Fallback: Query without orderBy if index doesn't exist
    const q = query(
      collection(db, COLLECTIONS.GALLERY),
      where("active", "==", true)
    );
    const snapshot = await getDocs(q);
    console.log("✅ Gallery query without order successful, items:", snapshot.size);
    const items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as GalleryItem[];
    
    // Sort in memory by order field if it exists
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  }
}

export async function getAllGalleryItems(): Promise<GalleryItem[]> {
  const q = query(
    collection(db, COLLECTIONS.GALLERY),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as GalleryItem[];
}

export async function getFeaturedGalleryItems(): Promise<GalleryItem[]> {
  const q = query(
    collection(db, COLLECTIONS.GALLERY),
    where("active", "==", true),
    where("featured", "==", true),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as GalleryItem[];
}

export async function getGalleryItemsByCategory(category: string): Promise<GalleryItem[]> {
  const q = query(
    collection(db, COLLECTIONS.GALLERY),
    where("active", "==", true),
    where("category", "==", category),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as GalleryItem[];
}

export async function updateGalleryItem(galleryId: string, data: Partial<InsertGalleryItem>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GALLERY, galleryId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function deleteGalleryItem(galleryId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GALLERY, galleryId);
  await deleteDoc(docRef);
}

export async function incrementGalleryViews(galleryId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GALLERY, galleryId);
  await updateDoc(docRef, {
    views: increment(1),
  });
}

// ============================================================================
// GALLERY PAGE SETTINGS
// ============================================================================

export async function getGalleryPageSettings(): Promise<GalleryPageSettings | null> {
  const docRef = doc(db, "system", "galleryPageSettings");
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    // Return default settings
    return {
      id: "galleryPageSettings",
      categoryTabs: {
        beforeAfter: { enabled: true, label: "Önce/Sonra", description: "" },
        interior: { enabled: true, label: "İç Temizlik", description: "" },
        exterior: { enabled: true, label: "Dış Yıkama", description: "" },
      },
      heroTitle: "Öncesi ve Sonrası",
      heroSubtitle: "Müşterilerimizin araçlarına verdiğimiz özenin fotoğrafları",
      updatedAt: Date.now(),
      updatedBy: "system",
    } as GalleryPageSettings;
  }
  
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as GalleryPageSettings;
}

export async function updateGalleryPageSettings(data: Partial<InsertGalleryPageSettings>, adminId: string): Promise<void> {
  const docRef = doc(db, "system", "galleryPageSettings");
  
  // Get current settings or defaults
  const current = await getGalleryPageSettings();
  
  if (!current) {
    throw new Error("Failed to load current gallery page settings");
  }
  
  // Deep merge with defaults to ensure all required fields exist
  const merged = {
    ...current,
    ...data,
    categoryTabs: {
      ...current.categoryTabs,
      ...(data.categoryTabs || {}),
      beforeAfter: {
        ...current.categoryTabs.beforeAfter,
        ...(data.categoryTabs?.beforeAfter || {}),
      },
      interior: {
        ...current.categoryTabs.interior,
        ...(data.categoryTabs?.interior || {}),
      },
      exterior: {
        ...current.categoryTabs.exterior,
        ...(data.categoryTabs?.exterior || {}),
      },
    },
    updatedAt: Date.now(),
    updatedBy: adminId,
  };
  
  await setDoc(docRef, merged);
}

// ============================================================================
// GALLERY CATEGORIES (Dynamic Categories)
// ============================================================================

export async function getGalleryCategories(): Promise<GalleryCategory_Type[]> {
  const q = query(
    collection(db, COLLECTIONS.GALLERY_CATEGORIES),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as GalleryCategory_Type[];
}

export async function getActiveGalleryCategories(): Promise<GalleryCategory_Type[]> {
  const q = query(
    collection(db, COLLECTIONS.GALLERY_CATEGORIES),
    where("active", "==", true),
    where("showInGallery", "==", true),
    orderBy("order", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as GalleryCategory_Type[];
}

export async function createGalleryCategory(data: InsertGalleryCategory, adminId: string): Promise<GalleryCategory_Type> {
  const categoryRef = doc(collection(db, COLLECTIONS.GALLERY_CATEGORIES));
  
  const categoryData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    updatedBy: adminId,
  };
  
  await setDoc(categoryRef, categoryData);
  return { id: categoryRef.id, ...categoryData } as GalleryCategory_Type;
}

export async function updateGalleryCategory(categoryId: string, data: Partial<InsertGalleryCategory>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GALLERY_CATEGORIES, categoryId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function deleteGalleryCategory(categoryId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.GALLERY_CATEGORIES, categoryId);
  await deleteDoc(docRef);
}

// ============================================================================
// DEPLOYMENT SETTINGS
// ============================================================================

export async function getDeploymentSettings(): Promise<DeploymentSettings | null> {
  const docRef = doc(db, COLLECTIONS.DEPLOYMENT_SETTINGS, "default");
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as DeploymentSettings;
}

export async function updateDeploymentSettings(data: Partial<InsertDeploymentSettings>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.DEPLOYMENT_SETTINGS, "default");
  await setDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  }, { merge: true });
}

// ============================================================================
// BLOG POSTS
// ============================================================================

export async function createBlogPost(data: InsertBlogPost, adminId: string): Promise<BlogPost> {
  const blogRef = doc(collection(db, COLLECTIONS.BLOG_POSTS));
  const blogData = {
    ...data,
    author: adminId,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    updatedBy: adminId,
  };
  await setDoc(blogRef, blogData);
  
  return { id: blogRef.id, ...blogData } as BlogPost;
}

export async function getPublishedBlogPosts(): Promise<BlogPost[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.BLOG_POSTS),
      where("status", "==", "published"),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    console.log("📰 Published blogs count:", snapshot.docs.length);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as BlogPost[];
  } catch (error) {
    console.error("❌ Error fetching published blogs:", error);
    return [];
  }
}

export async function getAllBlogPosts(): Promise<BlogPost[]> {
  const q = query(
    collection(db, COLLECTIONS.BLOG_POSTS),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as BlogPost[];
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const q = query(
    collection(db, COLLECTIONS.BLOG_POSTS),
    where("slug", "==", slug),
    where("status", "==", "published")
  );
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as BlogPost;
}

export async function getBlogPostsByCategory(category: string): Promise<BlogPost[]> {
  const q = query(
    collection(db, COLLECTIONS.BLOG_POSTS),
    where("status", "==", "published"),
    where("category", "==", category),
    orderBy("publishedAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as BlogPost[];
}

export async function getBlogPostsByTag(tag: string): Promise<BlogPost[]> {
  const q = query(
    collection(db, COLLECTIONS.BLOG_POSTS),
    where("status", "==", "published"),
    where("tags", "array-contains", tag),
    orderBy("publishedAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as BlogPost[];
}

export async function updateBlogPost(blogId: string, data: Partial<InsertBlogPost>, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.BLOG_POSTS, blogId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function publishBlogPost(blogId: string, adminId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.BLOG_POSTS, blogId);
  await updateDoc(docRef, {
    status: "published",
    publishedAt: Date.now(),
    updatedAt: Date.now(),
    updatedBy: adminId,
  });
}

export async function deleteBlogPost(blogId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.BLOG_POSTS, blogId);
  await deleteDoc(docRef);
}

export async function incrementBlogViews(blogId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.BLOG_POSTS, blogId);
  await updateDoc(docRef, {
    views: increment(1),
  });
}

export async function incrementBlogLikes(blogId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.BLOG_POSTS, blogId);
  await updateDoc(docRef, {
    likes: increment(1),
  });
}

// ============================================================================
// POPUP MANAGEMENT
// ============================================================================

export async function createPopup(data: InsertPopup): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.POPUPS), {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return docRef.id;
}

export async function getAllPopups(): Promise<Popup[]> {
  try {
    // Try with composite index first
    const q = query(
      collection(db, COLLECTIONS.POPUPS),
      orderBy("priority", "desc"),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Popup[];
  } catch (error) {
    // Fallback: Get all popups without ordering, sort client-side
    console.warn("getAllPopups: Using fallback query (index may be missing)", error);
    const snapshot = await getDocs(collection(db, COLLECTIONS.POPUPS));
    const popups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Popup[];
    // Sort client-side: priority desc, then createdAt desc
    return popups.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  }
}

export async function getPopupsByPage(pageId: string): Promise<Popup[]> {
  console.log("🔍 [getPopupsByPage] Looking for popups with pageId:", pageId);
  
  try {
    // Try with composite index first
    const q = query(
      collection(db, COLLECTIONS.POPUPS),
      where("pageId", "==", pageId),
      where("enabled", "==", true),
      orderBy("priority", "desc")
    );
    const snapshot = await getDocs(q);
    const result = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Popup[];
    console.log("✅ [getPopupsByPage] Found", result.length, "popups with index query");
    return result;
  } catch (error) {
    // Fallback: Get all popups, filter and sort client-side
    console.warn("getPopupsByPage: Using fallback query (index may be missing)", error);
    const snapshot = await getDocs(collection(db, COLLECTIONS.POPUPS));
    const allPopups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as Popup[];
    
    // Debug: Log all popups and their pageIds
    console.log("📋 [getPopupsByPage] All popups in database:", allPopups.map(p => ({
      id: p.id,
      title: p.title,
      pageId: p.pageId,
      enabled: p.enabled
    })));
    
    // Filter and sort client-side
    const filtered = allPopups
      .filter(p => p.pageId === pageId && p.enabled === true);
    
    console.log("🎯 [getPopupsByPage] Filtered popups for pageId='" + pageId + "':", filtered.length);
    
    return filtered.sort((a, b) => b.priority - a.priority);
  }
}

export async function getPopup(popupId: string): Promise<Popup | null> {
  const docRef = doc(db, COLLECTIONS.POPUPS, popupId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) return null;
  
  return {
    id: snapshot.id,
    ...convertTimestamps(snapshot.data()),
  } as Popup;
}

export async function updatePopup(popupId: string, data: Partial<InsertPopup>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.POPUPS, popupId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deletePopup(popupId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.POPUPS, popupId);
  await deleteDoc(docRef);
}

// ============================================================================
// EMAIL TEMPLATES
// ============================================================================

export async function getEmailTemplates(): Promise<any[]> {
  const q = query(collection(db, COLLECTIONS.EMAIL_TEMPLATES), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function createEmailTemplate(data: any): Promise<any> {
  const templateData = {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.EMAIL_TEMPLATES), templateData);
  return { id: docRef.id, ...templateData };
}

export async function updateEmailTemplate(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.EMAIL_TEMPLATES, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function getEmailLogs(limitCount: number = 100): Promise<any[]> {
  const q = query(
    collection(db, COLLECTIONS.EMAIL_LOGS),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

export async function getEmailSettings(): Promise<any | null> {
  const q = query(collection(db, COLLECTIONS.EMAIL_SETTINGS), limit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...convertTimestamps(snapshot.docs[0].data()) };
}

export async function updateEmailSettings(id: string, data: Partial<any>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.EMAIL_SETTINGS, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

// ============================================================================
// CORPORATE PRICING MANAGEMENT
// ============================================================================

export async function getCorporatePricing(companyId: string): Promise<CorporatePricing[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_PRICING),
    where("companyId", "==", companyId),
    where("isActive", "==", true),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporatePricing[];
}

export async function getAllCorporatePricing(): Promise<CorporatePricing[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_PRICING),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporatePricing[];
}

export async function createCorporatePricing(data: InsertCorporatePricing): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_PRICING), {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return docRef.id;
}

export async function updateCorporatePricing(pricingId: string, data: Partial<InsertCorporatePricing>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_PRICING, pricingId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteCorporatePricing(pricingId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_PRICING, pricingId);
  await deleteDoc(docRef);
}

// ============================================================================
// CORPORATE INVOICE MANAGEMENT
// ============================================================================

export async function getCorporateInvoices(companyId: string): Promise<CorporateInvoice[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_INVOICES),
    where("companyId", "==", companyId),
    orderBy("issueDate", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateInvoice[];
}

export async function getAllCorporateInvoices(): Promise<CorporateInvoice[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_INVOICES),
    orderBy("issueDate", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateInvoice[];
}

export async function createCorporateInvoice(data: InsertCorporateInvoice): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_INVOICES), {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return docRef.id;
}

export async function updateCorporateInvoice(invoiceId: string, data: Partial<InsertCorporateInvoice>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_INVOICES, invoiceId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteCorporateInvoice(invoiceId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_INVOICES, invoiceId);
  await deleteDoc(docRef);
}

// ============================================================================
// COMPANY-SPECIFIC DATA QUERIES
// ============================================================================

export async function getCompanyWashes(companyId: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("companyId", "==", companyId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function getCompanyPayments(companyId: string): Promise<Payment[]> {
  const q = query(
    collection(db, COLLECTIONS.PAYMENTS),
    where("companyId", "==", companyId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Payment[];
}

// ============================================================================
// USER NOTIFICATIONS (Subcollection: users/{userId}/notifications)
// ============================================================================

export async function getUserNotifications(userId: string): Promise<UserNotification[]> {
  const notificationsRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS);
  const q = query(notificationsRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as UserNotification[];
}

export async function getUserNotification(userId: string, notificationId: string): Promise<UserNotification | null> {
  const notificationRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS, notificationId);
  const notificationDoc = await getDoc(notificationRef);
  
  if (!notificationDoc.exists()) return null;
  return { id: notificationDoc.id, ...convertTimestamps(notificationDoc.data()) } as UserNotification;
}

export async function createUserNotification(userId: string, data: InsertUserNotification): Promise<string> {
  const notificationsRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS);
  const docRef = await addDoc(notificationsRef, {
    ...data,
    userId,
    read: data.read ?? false,
    createdAt: Date.now(),
  });
  return docRef.id;
}

export async function markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
  const notificationRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS, notificationId);
  await updateDoc(notificationRef, {
    read: true,
  });
}

export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  const notificationsRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS);
  const q = query(notificationsRef, where("read", "==", false));
  const snapshot = await getDocs(q);
  
  const updates = snapshot.docs.map(doc => 
    updateDoc(doc.ref, { read: true })
  );
  
  await Promise.all(updates);
}

export async function deleteUserNotification(userId: string, notificationId: string): Promise<void> {
  const notificationRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.NOTIFICATIONS, notificationId);
  await deleteDoc(notificationRef);
}

// ============================================================================
// CUSTOMER ANALYTICS
// ============================================================================

export async function getCustomerAnalytics(customerId: string): Promise<CustomerAnalytics | null> {
  const docRef = doc(db, COLLECTIONS.CUSTOMER_ANALYTICS, customerId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as CustomerAnalytics;
}

export async function getAllCustomerAnalytics(): Promise<CustomerAnalytics[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.CUSTOMER_ANALYTICS));
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerAnalytics[];
}

export async function getTopCustomersBySpending(limitCount: number = 10): Promise<CustomerAnalytics[]> {
  const q = query(
    collection(db, COLLECTIONS.CUSTOMER_ANALYTICS),
    orderBy("totalSpent", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerAnalytics[];
}

export async function getTopCustomersByVisits(limitCount: number = 10): Promise<CustomerAnalytics[]> {
  const q = query(
    collection(db, COLLECTIONS.CUSTOMER_ANALYTICS),
    orderBy("visitCount", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerAnalytics[];
}

export async function getVIPCustomers(): Promise<CustomerAnalytics[]> {
  const q = query(
    collection(db, COLLECTIONS.CUSTOMER_ANALYTICS),
    where("isVIP", "==", true),
    orderBy("totalSpent", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerAnalytics[];
}

export async function createOrUpdateCustomerAnalytics(customerId: string, data: Partial<InsertCustomerAnalytics>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CUSTOMER_ANALYTICS, customerId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    await updateDoc(docRef, {
      ...data,
      updatedAt: Date.now(),
    });
  } else {
    await setDoc(docRef, {
      customerId,
      totalSpent: 0,
      visitCount: 0,
      lastVisitDate: null,
      firstVisitDate: null,
      favoriteServiceIds: [],
      averageTicketSize: 0,
      loyaltyTier: "bronze",
      monthlySpendHistory: [],
      vehicleCount: 0,
      isVIP: false,
      ...data,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }
}

// ============================================================================
// CORPORATE AGREEMENTS
// ============================================================================

export async function getCorporateAgreement(agreementId: string): Promise<CorporateAgreement | null> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_AGREEMENTS, agreementId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as CorporateAgreement;
}

export async function getCorporateAgreementsByCompany(companyId: string): Promise<CorporateAgreement[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_AGREEMENTS),
    where("companyId", "==", companyId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateAgreement[];
}

export async function getActiveAgreementsByCompany(companyId: string): Promise<CorporateAgreement[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_AGREEMENTS),
    where("companyId", "==", companyId),
    where("status", "==", "active"),
    orderBy("endDate", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateAgreement[];
}

export async function getAllCorporateAgreements(): Promise<CorporateAgreement[]> {
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_AGREEMENTS),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateAgreement[];
}

export async function getExpiringAgreements(daysUntilExpiry: number = 30): Promise<CorporateAgreement[]> {
  const expiryDate = Date.now() + (daysUntilExpiry * 24 * 60 * 60 * 1000);
  
  const q = query(
    collection(db, COLLECTIONS.CORPORATE_AGREEMENTS),
    where("status", "==", "active"),
    where("endDate", "<=", expiryDate),
    orderBy("endDate", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CorporateAgreement[];
}

export async function createCorporateAgreement(data: InsertCorporateAgreement): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.CORPORATE_AGREEMENTS), {
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return docRef.id;
}

export async function updateCorporateAgreement(agreementId: string, data: Partial<InsertCorporateAgreement>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_AGREEMENTS, agreementId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteCorporateAgreement(agreementId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.CORPORATE_AGREEMENTS, agreementId);
  await deleteDoc(docRef);
}

// ============================================================================
// VEHICLE ANALYTICS
// ============================================================================

export async function getVehicleAnalytics(vehicleId: string): Promise<VehicleAnalytics | null> {
  const docRef = doc(db, COLLECTIONS.VEHICLE_ANALYTICS, vehicleId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as VehicleAnalytics;
}

export async function getAllVehicleAnalytics(): Promise<VehicleAnalytics[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.VEHICLE_ANALYTICS));
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as VehicleAnalytics[];
}

export async function getTopVehiclesByWashCount(limitCount: number = 10): Promise<VehicleAnalytics[]> {
  const q = query(
    collection(db, COLLECTIONS.VEHICLE_ANALYTICS),
    orderBy("totalWashCount", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as VehicleAnalytics[];
}

export async function getTopVehiclesByRevenue(limitCount: number = 10): Promise<VehicleAnalytics[]> {
  const q = query(
    collection(db, COLLECTIONS.VEHICLE_ANALYTICS),
    orderBy("totalRevenue", "desc"),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as VehicleAnalytics[];
}

export async function createOrUpdateVehicleAnalytics(vehicleId: string, data: Partial<InsertVehicleAnalytics>): Promise<void> {
  const docRef = doc(db, COLLECTIONS.VEHICLE_ANALYTICS, vehicleId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    await updateDoc(docRef, {
      ...data,
      updatedAt: Date.now(),
    });
  } else {
    await setDoc(docRef, {
      vehicleId,
      totalWashCount: 0,
      totalRevenue: 0,
      firstWashDate: null,
      lastWashDate: null,
      serviceUsageCount: {},
      mostUsedServiceId: null,
      averageTicketSize: 0,
      monthlyWashHistory: [],
      ...data,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }
}

// ============================================================================
// ADVANCED ANALYTICS QUERIES
// ============================================================================

export async function getCustomerWashHistory(customerId: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("customerId", "==", customerId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function getVehicleWashHistory(vehicleId: string): Promise<Wash[]> {
  const q = query(
    collection(db, COLLECTIONS.WASHES),
    where("vehicleId", "==", vehicleId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as Wash[];
}

export async function searchVehicleByPlate(plateNumber: string): Promise<Vehicle | null> {
  const normalizedPlate = plateNumber.toUpperCase().replace(/\s/g, '');
  
  const q = query(
    collection(db, COLLECTIONS.VEHICLES),
    where("plateNumber", "==", normalizedPlate),
    limit(1)
  );
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...convertTimestamps(doc.data()) } as Vehicle;
}

// ============================================================================
// SERVER-SIDE AGGREGATED ANALYTICS
// ============================================================================

export interface DailyAnalytics {
  id: string;
  date: string;
  createdAt: number;
  revenue: {
    total: number;
    cash: number;
    card: number;
    byService: Record<string, number>;
  };
  washes: {
    total: number;
    completed: number;
    byStatus: Record<string, number>;
    bySource: Record<string, number>;
  };
  customers: {
    total: number;
    new: number;
    returning: number;
    loyaltyTier: Record<string, number>;
  };
  topServices: Array<{ serviceId: string; count: number; revenue: number }>;
  topCorporateClients: Array<{ companyId: string; washCount: number; revenue: number }>;
}

export interface MonthlyAnalytics {
  id: string;
  month: string;
  createdAt: number;
  revenue: {
    total: number;
    cash: number;
    card: number;
    byService: Record<string, number>;
  };
  washes: {
    total: number;
    completed: number;
    byStatus: Record<string, number>;
    bySource: Record<string, number>;
  };
  customers: {
    total: number;
    new: number;
    returning: number;
    loyaltyTier: Record<string, number>;
  };
  topServices: Array<{ serviceId: string; count: number; revenue: number }>;
  topCorporateClients: Array<{ companyId: string; washCount: number; revenue: number }>;
  dailyBreakdown: DailyAnalytics[];
}

/**
 * Get daily analytics (pre-aggregated by Cloud Functions)
 * @param dateStr Date in YYYY-MM-DD format
 * @returns DailyAnalytics or null if not found
 */
export async function getDailyAnalytics(dateStr: string): Promise<DailyAnalytics | null> {
  try {
    const docRef = doc(db, 'analytics_daily', dateStr);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No daily analytics found for ${dateStr}`);
      return null;
    }
    
    return {
      id: docSnap.id,
      ...convertTimestamps(docSnap.data()),
    } as DailyAnalytics;
  } catch (error) {
    console.error('Error fetching daily analytics:', error);
    return null;
  }
}

/**
 * Get monthly analytics (pre-aggregated by Cloud Functions)
 * @param monthStr Month in YYYY-MM format (e.g., "2025-11")
 * @returns MonthlyAnalytics or null if not found
 */
export async function getMonthlyAnalytics(monthStr: string): Promise<MonthlyAnalytics | null> {
  try {
    const docRef = doc(db, 'analytics_monthly', monthStr);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No monthly analytics found for ${monthStr}`);
      return null;
    }
    
    return {
      id: docSnap.id,
      ...convertTimestamps(docSnap.data()),
    } as MonthlyAnalytics;
  } catch (error) {
    console.error('Error fetching monthly analytics:', error);
    return null;
  }
}

/**
 * Get analytics for a date range (fetches multiple daily analytics)
 * @param startDate Start date in YYYY-MM-DD format
 * @param endDate End date in YYYY-MM-DD format
 * @returns Array of DailyAnalytics
 */
export async function getAnalyticsByDateRange(
  startDate: string,
  endDate: string
): Promise<DailyAnalytics[]> {
  try {
    const q = query(
      collection(db, 'analytics_daily'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`No analytics found for range ${startDate} to ${endDate}`);
      return [];
    }
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as DailyAnalytics[];
  } catch (error) {
    console.error('Error fetching analytics by date range:', error);
    return [];
  }
}

// ============================================================================
// SUBSCRIPTION PACKAGE FUNCTIONS
// ============================================================================

export async function getAllSubscriptionPackages(): Promise<SubscriptionPackage[]> {
  const snapshot = await getDocs(
    query(collection(db, "subscription_packages"), orderBy("displayOrder", "asc"))
  );
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as SubscriptionPackage[];
}

export async function getActiveSubscriptionPackages(): Promise<SubscriptionPackage[]> {
  const snapshot = await getDocs(
    query(
      collection(db, "subscription_packages"),
      where("active", "==", true),
      orderBy("displayOrder", "asc")
    )
  );
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as SubscriptionPackage[];
}

export async function getSubscriptionPackage(packageId: string): Promise<SubscriptionPackage | null> {
  console.log("📦 [getSubscriptionPackage] Fetching package:", packageId);
  const docRef = doc(db, "subscription_packages", packageId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    console.warn("⚠️ [getSubscriptionPackage] Package not found:", packageId);
    return null;
  }
  
  const pkg = {
    id: docSnap.id,
    ...convertTimestamps(docSnap.data()),
  } as SubscriptionPackage;
  console.log("✅ [getSubscriptionPackage] Package found:", { id: pkg.id, name: pkg.name });
  return pkg;
}

export async function createSubscriptionPackage(data: InsertSubscriptionPackage): Promise<SubscriptionPackage> {
  const timestamp = Date.now();
  const packageData = {
    ...data,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  
  const docRef = await addDoc(collection(db, "subscription_packages"), packageData);
  
  return {
    id: docRef.id,
    ...data,
    createdAt: timestamp,
    updatedAt: timestamp,
  } as SubscriptionPackage;
}

export async function updateSubscriptionPackage(
  packageId: string,
  data: Partial<SubscriptionPackage>
): Promise<void> {
  const docRef = doc(db, "subscription_packages", packageId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

export async function deleteSubscriptionPackage(packageId: string): Promise<void> {
  const docRef = doc(db, "subscription_packages", packageId);
  await deleteDoc(docRef);
}

// ============================================================================
// CUSTOMER SUBSCRIPTION FUNCTIONS
// ============================================================================

export async function getCustomerSubscriptions(userId: string): Promise<CustomerSubscription[]> {
  console.log("📋 [getCustomerSubscriptions] Fetching ALL subscriptions for userId:", userId);
  
  try {
    const snapshot = await getDocs(
      query(
        collection(db, "customer_subscriptions"),
        where("userId", "==", userId),
        orderBy("createdAt", "desc")
      )
    );
    
    console.log("📋 [getCustomerSubscriptions] Found documents:", snapshot.docs.length);
    
    const subscriptions = snapshot.docs.map(doc => {
      const data = convertTimestamps(doc.data());
      console.log("📋 [getCustomerSubscriptions] Document:", doc.id, {
        status: data.status,
        paymentStatus: data.paymentStatus,
        packageId: data.packageId,
      });
      return {
        id: doc.id,
        ...data,
      };
    }) as CustomerSubscription[];
    
    console.log("📋 [getCustomerSubscriptions] Returning subscriptions:", subscriptions.length);
    return subscriptions;
  } catch (error: any) {
    console.error("❌ [getCustomerSubscriptions] ERROR:", error);
    console.error("❌ [getCustomerSubscriptions] Error code:", error.code);
    console.error("❌ [getCustomerSubscriptions] Error message:", error.message);
    throw error;
  }
}

export async function getActiveCustomerSubscriptions(userId: string): Promise<CustomerSubscription[]> {
  console.log("[getActiveCustomerSubscriptions] Fetching for userId:", userId);
  try {
    const now = Date.now();
    const snapshot = await getDocs(
      query(
        collection(db, "customer_subscriptions"),
        where("userId", "==", userId),
        where("status", "==", "active"),
        where("paymentStatus", "==", "paid"), // ✅ CRITICAL: Only paid subscriptions
        where("endDate", ">", now), // ✅ Only non-expired subscriptions
        orderBy("endDate", "asc")
      )
    );
    console.log("[getActiveCustomerSubscriptions] Found docs:", snapshot.docs.length);
    const results = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as CustomerSubscription[];
    console.log("[getActiveCustomerSubscriptions] Results:", results);
    return results;
  } catch (error: any) {
    console.error("[getActiveCustomerSubscriptions] ERROR:", error);
    console.error("[getActiveCustomerSubscriptions] Error message:", error.message);
    console.error("[getActiveCustomerSubscriptions] Error code:", error.code);
    
    // Fallback: Try simpler query without compound index (for debugging)
    console.log("[getActiveCustomerSubscriptions] Trying fallback query without date filter...");
    try {
      const fallbackSnapshot = await getDocs(
        query(
          collection(db, "customer_subscriptions"),
          where("userId", "==", userId),
          where("status", "==", "active"),
          where("paymentStatus", "==", "paid"), // ✅ CRITICAL: Only paid subscriptions
          orderBy("createdAt", "desc")
        )
      );
      console.log("[getActiveCustomerSubscriptions] Fallback found docs:", fallbackSnapshot.docs.length);
      const fallbackResults = fallbackSnapshot.docs.map(doc => {
        const data = convertTimestamps(doc.data());
        console.log("[getActiveCustomerSubscriptions] Fallback doc:", doc.id, data);
        return { id: doc.id, ...data };
      }) as CustomerSubscription[];
      
      // Filter expired ones manually + ensure payment is complete
      const now = Date.now();
      const activeResults = fallbackResults.filter(sub => 
        sub.endDate > now && sub.paymentStatus === 'paid'
      );
      console.log("[getActiveCustomerSubscriptions] After manual filtering:", activeResults.length);
      return activeResults;
    } catch (fallbackError: any) {
      console.error("[getActiveCustomerSubscriptions] Fallback also failed:", fallbackError);
      throw error; // Throw original error
    }
  }
}

export async function getPendingCustomerSubscriptions(): Promise<CustomerSubscription[]> {
  console.log("🔍 [getPendingCustomerSubscriptions] Querying pending subscriptions...");
  
  try {
    const snapshot = await getDocs(
      query(
        collection(db, "customer_subscriptions"),
        where("status", "==", "pending"), // ✅ Status pending = awaiting cashier approval
        orderBy("createdAt", "desc")
      )
    );
    
    console.log(`✅ [getPendingCustomerSubscriptions] Found ${snapshot.docs.length} pending subscriptions`);
    const results = snapshot.docs.map(doc => ({
      id: doc.id,
      ...convertTimestamps(doc.data()),
    })) as CustomerSubscription[];
    console.log("📋 [getPendingCustomerSubscriptions] Results:", results.map(r => ({ 
      id: r.id, 
      status: r.status, 
      paymentStatus: r.paymentStatus, 
      userId: r.userId,
      packageId: r.packageId,
      vehicleIds: r.vehicleIds,
      hasAllFields: !!(r.userId && r.packageId)
    })));
    return results;
  } catch (error: any) {
    console.error("❌ [getPendingCustomerSubscriptions] Query failed:", error.message);
    console.error("❌ [getPendingCustomerSubscriptions] Error details:", error);
    
    // Check if it's an index error
    if (error.message?.includes("index")) {
      console.warn("⚠️ [getPendingCustomerSubscriptions] Firestore index is building. This may take a few minutes.");
      console.warn("⚠️ Index URL:", error.message);
    }
    
    throw error; // Re-throw so UI can handle it
  }
}

export async function approveCustomerSubscription(subscriptionId: string): Promise<void> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  await updateDoc(docRef, {
    status: "active",
    paymentStatus: "paid",
    approvedAt: Date.now(),
    updatedAt: Date.now(),
  });
}

export async function getCustomerSubscription(subscriptionId: string): Promise<CustomerSubscription | null> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...convertTimestamps(docSnap.data()),
  } as CustomerSubscription;
}

export async function getCompanySubscriptions(companyId: string): Promise<CustomerSubscription[]> {
  const snapshot = await getDocs(
    query(
      collection(db, "customer_subscriptions"),
      where("companyId", "==", companyId),
      orderBy("createdAt", "desc")
    )
  );
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerSubscription[];
}

export async function getAllCustomerSubscriptions(): Promise<CustomerSubscription[]> {
  const snapshot = await getDocs(
    query(collection(db, "customer_subscriptions"), orderBy("createdAt", "desc"))
  );
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerSubscription[];
}

export async function getExpiringSubscriptions(days: number = 7): Promise<CustomerSubscription[]> {
  const now = Date.now();
  const futureTime = now + (days * 24 * 60 * 60 * 1000);
  
  const snapshot = await getDocs(
    query(
      collection(db, "customer_subscriptions"),
      where("status", "==", "active"),
      where("endDate", "<=", futureTime),
      where("endDate", ">=", now),
      orderBy("endDate", "asc")
    )
  );
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as CustomerSubscription[];
}

export async function createCustomerSubscription(data: InsertCustomerSubscription): Promise<CustomerSubscription> {
  console.log('🔵 [createCustomerSubscription] Creating subscription with client-side approach:', {
    packageId: data.packageId,
    vehicleIds: data.vehicleIds,
    userId: data.userId,
  });
  
  try {
    // 1. Fetch package data for validation and pricing
    const packageDoc = await getDoc(doc(db, "subscription_packages", data.packageId));
    if (!packageDoc.exists()) {
      throw new Error('Seçilen paket bulunamadı');
    }
    
    const packageData = packageDoc.data();
    if (!packageData.active) {
      throw new Error('Seçilen paket aktif değil');
    }
    
    console.log('📦 [createCustomerSubscription] Package data:', packageData);
    
    // 2. Validate vehicle count
    if (data.vehicleIds.length === 0) {
      throw new Error('En az bir araç seçmelisiniz');
    }
    
    if (data.vehicleIds.length > packageData.maxVehicles) {
      throw new Error(`Bu paket maksimum ${packageData.maxVehicles} araç için geçerlidir`);
    }
    
    // 3. Verify all vehicles belong to user (client-side check before Firestore validation)
    const vehicleChecks = await Promise.all(
      data.vehicleIds.map(async (vehicleId) => {
        const vehicleDoc = await getDoc(doc(db, "vehicles", vehicleId));
        return {
          exists: vehicleDoc.exists(),
          userId: vehicleDoc.data()?.userId,
          vehicleId,
        };
      })
    );
    
    const invalidVehicles = vehicleChecks.filter(
      (check) => !check.exists || check.userId !== data.userId
    );
    
    if (invalidVehicles.length > 0) {
      console.error('❌ Invalid vehicles detected:', invalidVehicles);
      throw new Error('Seçilen araçlardan bazıları size ait değil');
    }
    
    console.log('✅ All vehicles verified');
    
    // 4. Calculate dates based on package type
    const now = Date.now();
    let duration = 30 * 24 * 60 * 60 * 1000; // Default: 1 month
    
    switch (packageData.type) {
      case 'quarterly':
        duration = 90 * 24 * 60 * 60 * 1000; // 3 months
        break;
      case 'six_monthly':
        duration = 180 * 24 * 60 * 60 * 1000; // 6 months
        break;
      case 'annual':
        duration = 365 * 24 * 60 * 60 * 1000; // 1 year
        break;
      default:
        duration = 30 * 24 * 60 * 60 * 1000; // 1 month
    }
    
    const endDate = now + duration;
    
    // 5. Create subscription document with server-validated data
    const subscriptionData: any = {
      packageId: data.packageId,
      userId: data.userId,
      status: 'pending', // ✅ Pending until cashier confirms payment
      startDate: now,
      endDate,
      // LEGACY quotas (backward compatibility)
      remainingExteriorWashes: packageData.exteriorWashCount || 0,
      remainingInteriorCleans: packageData.interiorCleanCount || 0,
      remainingDetailings: packageData.detailingCount || 0,
      usedExteriorWashes: 0,
      usedInteriorCleans: 0,
      usedDetailings: 0,
      vehicleIds: data.vehicleIds,
      purchasePrice: packageData.price || 0,
      paymentStatus: 'pending', // ✅ Pending payment - awaits cashier approval
      autoRenew: false,
      notifyBeforeExpiry: true,
      notifyDaysBefore: 7,
      createdAt: now,
      updatedAt: now,
    };
    
    // NEW: Add service-based quotas if package has them
    if (packageData.serviceQuotas && Array.isArray(packageData.serviceQuotas) && packageData.serviceQuotas.length > 0) {
      subscriptionData.serviceQuotas = packageData.serviceQuotas.map((quota: any) => ({
        serviceId: quota.serviceId,
        remaining: quota.quantity,
        used: 0,
      }));
      console.log('✅ [createCustomerSubscription] Added serviceQuotas:', subscriptionData.serviceQuotas);
    }
    
    console.log('📝 [createCustomerSubscription] Creating subscription in Firestore...');
    
    const subscriptionRef = await addDoc(collection(db, "customer_subscriptions"), subscriptionData);
    
    console.log('✅ [createCustomerSubscription] Created subscription:', subscriptionRef.id);
    
    // 6. Fetch the created subscription to return complete data
    const createdDoc = await getDoc(subscriptionRef);
    const subscription = {
      id: createdDoc.id,
      ...convertTimestamps(createdDoc.data()),
    } as CustomerSubscription;
    
    console.log('🟢 [createCustomerSubscription] Success! Subscription:', subscription);
    
    return subscription;
    
  } catch (error: any) {
    console.error('❌ [createCustomerSubscription] Error:', error);
    console.error('❌ [createCustomerSubscription] Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
    });
    
    // Provide user-friendly error messages
    if (error.code === 'permission-denied') {
      throw new Error('Yetkilendirme hatası. Lütfen giriş yapıp tekrar deneyin.');
    }
    
    throw new Error(error.message || 'Abonelik oluşturulamadı. Lütfen tekrar deneyin.');
  }
}

export async function updateCustomerSubscription(
  subscriptionId: string,
  data: Partial<CustomerSubscription>
): Promise<void> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Date.now(),
  });
}

/**
 * Decrement subscription quotas when a wash is completed
 * Handles both new format (serviceQuotas) and legacy format (remainingExteriorWashes etc.)
 * Also creates usage history records in subscription_usage collection
 */
export async function decrementSubscriptionQuota(
  subscriptionId: string,
  serviceIds: string[],
  services: Array<{ id: string; category: string; name?: string }>,
  washId?: string,
  vehicleId?: string
): Promise<{ success: boolean; message: string }> {
  console.log('🔄 [decrementSubscriptionQuota] Starting quota decrement for subscription:', subscriptionId);
  console.log('🔄 [decrementSubscriptionQuota] Service IDs:', serviceIds);
  
  try {
    const subscription = await getCustomerSubscription(subscriptionId);
    if (!subscription) {
      console.error('❌ [decrementSubscriptionQuota] Subscription not found:', subscriptionId);
      return { success: false, message: 'Abonelik bulunamadı' };
    }
    
    const docRef = doc(db, "customer_subscriptions", subscriptionId);
    const updateData: any = { updatedAt: Date.now() };
    const usageRecords: Array<{ serviceId: string; serviceName: string; serviceType: string }> = [];
    
    // Check if subscription uses new serviceQuotas format
    if (subscription.serviceQuotas && Array.isArray(subscription.serviceQuotas) && subscription.serviceQuotas.length > 0) {
      console.log('📋 [decrementSubscriptionQuota] Using serviceQuotas format');
      
      // Decrement quotas for each service used
      const updatedQuotas = subscription.serviceQuotas.map(quota => {
        if (serviceIds.includes(quota.serviceId) && quota.remaining > 0) {
          console.log(`  ✅ Decrementing quota for service ${quota.serviceId}: ${quota.remaining} -> ${quota.remaining - 1}`);
          const service = services.find(s => s.id === quota.serviceId);
          usageRecords.push({
            serviceId: quota.serviceId,
            serviceName: service?.name || quota.serviceId,
            serviceType: service?.category || 'unknown',
          });
          return {
            ...quota,
            remaining: quota.remaining - 1,
            used: (quota.used || 0) + 1,
          };
        }
        return quota;
      });
      
      updateData.serviceQuotas = updatedQuotas;
    } else {
      console.log('📋 [decrementSubscriptionQuota] Using legacy format');
      
      // Legacy format - decrement based on service categories
      for (const serviceId of serviceIds) {
        const service = services.find(s => s.id === serviceId);
        if (!service) continue;
        
        const category = service.category;
        console.log(`  📦 Service ${serviceId} category: ${category}`);
        
        if (category === 'exterior' && (subscription.remainingExteriorWashes || 0) > 0) {
          updateData.remainingExteriorWashes = (subscription.remainingExteriorWashes || 0) - 1;
          updateData.usedExteriorWashes = (subscription.usedExteriorWashes || 0) + 1;
          usageRecords.push({ serviceId, serviceName: service.name || 'Dış Yıkama', serviceType: 'exterior' });
          console.log(`  ✅ Decremented exterior wash: ${subscription.remainingExteriorWashes} -> ${updateData.remainingExteriorWashes}`);
        } else if (category === 'interior' && (subscription.remainingInteriorCleans || 0) > 0) {
          updateData.remainingInteriorCleans = (subscription.remainingInteriorCleans || 0) - 1;
          updateData.usedInteriorCleans = (subscription.usedInteriorCleans || 0) + 1;
          usageRecords.push({ serviceId, serviceName: service.name || 'İç Temizlik', serviceType: 'interior' });
          console.log(`  ✅ Decremented interior clean: ${subscription.remainingInteriorCleans} -> ${updateData.remainingInteriorCleans}`);
        } else if (category === 'detailing' && (subscription.remainingDetailings || 0) > 0) {
          updateData.remainingDetailings = (subscription.remainingDetailings || 0) - 1;
          updateData.usedDetailings = (subscription.usedDetailings || 0) + 1;
          usageRecords.push({ serviceId, serviceName: service.name || 'Detaylı Temizlik', serviceType: 'detailing' });
          console.log(`  ✅ Decremented detailing: ${subscription.remainingDetailings} -> ${updateData.remainingDetailings}`);
        }
      }
    }
    
    await updateDoc(docRef, updateData);
    console.log('✅ [decrementSubscriptionQuota] Successfully updated subscription quotas');
    
    // Create usage history records for each service used
    for (const usage of usageRecords) {
      try {
        const usageRef = doc(collection(db, "subscription_usage"));
        await setDoc(usageRef, {
          subscriptionId,
          washId: washId || null,
          userId: subscription.userId,
          vehicleId: vehicleId || (subscription.vehicleIds?.[0] || null),
          serviceType: usage.serviceType,
          serviceId: usage.serviceId,
          serviceName: usage.serviceName,
          usedAt: Date.now(),
          createdAt: Date.now(),
        });
        console.log(`📝 [decrementSubscriptionQuota] Created usage record for service: ${usage.serviceName}`);
      } catch (usageError) {
        console.error('⚠️ [decrementSubscriptionQuota] Failed to create usage record:', usageError);
        // Don't fail the whole operation if usage record fails
      }
    }
    
    return { success: true, message: 'Abonelik hakları güncellendi' };
  } catch (error: any) {
    console.error('❌ [decrementSubscriptionQuota] Error:', error);
    return { success: false, message: error.message || 'Abonelik hakları güncellenemedi' };
  }
}

export async function cancelCustomerSubscription(subscriptionId: string): Promise<void> {
  console.log("🗑️ [cancelCustomerSubscription] Attempting to cancel subscription:", subscriptionId);
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  
  try {
    await updateDoc(docRef, {
      status: "cancelled",
      cancelledAt: Date.now(),
      updatedAt: Date.now(),
    });
    console.log("✅ [cancelCustomerSubscription] Successfully cancelled subscription:", subscriptionId);
  } catch (error: any) {
    console.error("❌ [cancelCustomerSubscription] Failed to cancel subscription:", {
      subscriptionId,
      errorCode: error.code,
      errorMessage: error.message,
      fullError: error
    });
    
    // Provide user-friendly error messages
    if (error.code === 'permission-denied') {
      throw new Error('Bu aboneliği iptal etme yetkiniz yok. Lütfen yetkili bir kullanıcı ile iletişime geçin.');
    }
    
    throw new Error(error.message || 'Abonelik iptal edilemedi. Lütfen tekrar deneyin.');
  }
}

export async function pauseCustomerSubscription(subscriptionId: string): Promise<void> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  await updateDoc(docRef, {
    status: "paused",
    pausedAt: Date.now(),
    updatedAt: Date.now(),
  });
}

export async function resumeCustomerSubscription(subscriptionId: string): Promise<void> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  await updateDoc(docRef, {
    status: "active",
    pausedAt: null,
    updatedAt: Date.now(),
  });
}

export async function recordSubscriptionPayment(
  subscriptionId: string,
  paymentMethod: string,
  cashierId: string
): Promise<void> {
  const timestamp = Date.now();
  const subscriptionRef = doc(db, "customer_subscriptions", subscriptionId);
  
  await runTransaction(db, async (transaction) => {
    const subscriptionDoc = await transaction.get(subscriptionRef);
    
    if (!subscriptionDoc.exists()) {
      throw new Error("Abonelik bulunamadı");
    }
    
    const subscription = subscriptionDoc.data() as CustomerSubscription;
    
    // Check if already paid
    if (subscription.paymentStatus === "paid") {
      throw new Error("Bu abonelik zaten ödenmiş");
    }
    
    // Update subscription payment status AND activate subscription
    transaction.update(subscriptionRef, {
      status: "active", // ✅ Activate subscription after payment
      paymentStatus: "paid",
      paymentMethod,
      paidAt: timestamp,
      cashierId,
      updatedAt: timestamp,
    });
    
    // Create payment audit record in separate collection
    const paymentRecordRef = doc(collection(db, "subscription_payment_records"));
    transaction.set(paymentRecordRef, {
      subscriptionId,
      userId: subscription.userId,
      packageId: subscription.packageId,
      amount: subscription.purchasePrice,
      paymentMethod,
      cashierId,
      paidAt: timestamp,
      createdAt: timestamp,
    });
  });
}

export interface SubscriptionPaymentRecord {
  id: string;
  subscriptionId: string;
  userId: string;
  packageId: string;
  amount: number;
  paymentMethod: string;
  cashierId: string;
  paidAt: number;
  createdAt: number;
}

export async function getSubscriptionPaymentRecords(): Promise<SubscriptionPaymentRecord[]> {
  const paymentsRef = collection(db, "subscription_payment_records");
  const querySnapshot = await getDocs(paymentsRef);
  
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as SubscriptionPaymentRecord[];
}

export async function useSubscriptionService(
  subscriptionId: string,
  serviceType: "exterior" | "interior" | "detailing" | "extra",
  washId: string,
  vehicleId: string,
  serviceId: string,
  serviceName: string
): Promise<void> {
  const subscriptionRef = doc(db, "customer_subscriptions", subscriptionId);
  
  await runTransaction(db, async (transaction) => {
    const subscriptionDoc = await transaction.get(subscriptionRef);
    
    if (!subscriptionDoc.exists()) {
      throw new Error("Abonelik bulunamadı");
    }
    
    const subscription = subscriptionDoc.data() as CustomerSubscription;
    
    // Check if service is available based on type
    let remainingKey: keyof CustomerSubscription;
    let usedKey: keyof CustomerSubscription;
    
    switch (serviceType) {
      case "exterior":
        remainingKey = "remainingExteriorWashes";
        usedKey = "usedExteriorWashes";
        break;
      case "interior":
        remainingKey = "remainingInteriorCleans";
        usedKey = "usedInteriorCleans";
        break;
      case "detailing":
        remainingKey = "remainingDetailings";
        usedKey = "usedDetailings";
        break;
      default:
        throw new Error("Geçersiz hizmet tipi");
    }
    
    const remaining = subscription[remainingKey] as number;
    if (remaining <= 0) {
      throw new Error(`${serviceType} hizmeti kotanız dolmuş`);
    }
    
    // Update subscription usage
    transaction.update(subscriptionRef, {
      [remainingKey]: remaining - 1,
      [usedKey]: increment(1),
      updatedAt: Date.now(),
    });
    
    // Create usage log
    const usageRef = doc(collection(db, "subscription_usage"));
    transaction.set(usageRef, {
      subscriptionId,
      washId,
      userId: subscription.userId,
      vehicleId,
      serviceType,
      serviceId,
      serviceName,
      usedAt: Date.now(),
      createdAt: Date.now(),
    });
  });
}

// ============================================================================
// SUBSCRIPTION USAGE FUNCTIONS
// ============================================================================

export async function getSubscriptionUsage(subscriptionId: string): Promise<SubscriptionUsage[]> {
  console.log('[getSubscriptionUsage] Fetching usage for subscription:', subscriptionId);
  
  try {
    // Use Cloud Function to bypass Firestore rules
    const getSubscriptionUsageSecure = httpsCallable(functions, 'getSubscriptionUsageSecure');
    const result = await getSubscriptionUsageSecure({ subscriptionId });
    const data = result.data as { success: boolean; usage: SubscriptionUsage[]; count: number };
    
    console.log('[getSubscriptionUsage] Cloud Function returned:', data.count, 'records');
    return data.usage || [];
  } catch (error: any) {
    console.error('[getSubscriptionUsage] Cloud Function error:', error);
    
    // Fallback: Try direct Firestore queries (may fail due to permissions)
    console.log('[getSubscriptionUsage] Attempting fallback queries...');
    
    let legacyUsage: SubscriptionUsage[] = [];
    try {
      const legacySnapshot = await getDocs(
        query(
          collection(db, "subscription_usage"),
          where("subscriptionId", "==", subscriptionId),
          orderBy("usedAt", "desc")
        )
      );
      legacyUsage = legacySnapshot.docs.map(doc => ({
        id: doc.id,
        ...convertTimestamps(doc.data()),
      })) as SubscriptionUsage[];
    } catch (e) {
      console.warn('[getSubscriptionUsage] Legacy collection fallback error:', e);
    }

    let washUsage: SubscriptionUsage[] = [];
    try {
      const washSnapshot = await getDocs(
        query(
          collection(db, COLLECTIONS.WASHES),
          where("subscriptionId", "==", subscriptionId)
        )
      );
      
      washUsage = washSnapshot.docs.map(doc => {
        const wash = convertTimestamps(doc.data());
        return {
          id: `wash_${doc.id}`,
          subscriptionId: subscriptionId,
          washId: doc.id,
          serviceId: wash.serviceIds?.[0] || '',
          serviceName: '',
          vehicleId: wash.vehicleId || '',
          usedAt: wash.createdAt || Date.now(),
          serviceType: 'exterior' as const,
          serviceIds: wash.serviceIds || [],
          status: wash.status,
          totalAmount: wash.totalAmount,
          finalAmount: wash.finalAmount,
        } as SubscriptionUsage;
      });
    } catch (e) {
      console.warn('[getSubscriptionUsage] Washes fallback error:', e);
    }

    const allUsage = [...legacyUsage];
    const existingWashIds = new Set(legacyUsage.map(u => u.washId).filter(Boolean));
    
    for (const wu of washUsage) {
      if (!existingWashIds.has(wu.washId)) {
        allUsage.push(wu);
      }
    }

    return allUsage.sort((a, b) => (b.usedAt || 0) - (a.usedAt || 0));
  }
}

export async function getAllSubscriptionUsage(): Promise<SubscriptionUsage[]> {
  const snapshot = await getDocs(
    query(collection(db, "subscription_usage"), orderBy("usedAt", "desc"), limit(500))
  );
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  })) as SubscriptionUsage[];
}

// ============================================================================
// CORPORATE BUDGET FUNCTIONS
// ============================================================================

export async function getCurrentBudget(corporateCustomerId: string, month: number, year: number): Promise<CorporateBudget | null> {
  const snapshot = await getDocs(
    query(
      collection(db, "corporate_budgets"),
      where("corporateCustomerId", "==", corporateCustomerId),
      where("month", "==", month),
      where("year", "==", year),
      limit(1)
    )
  );
  
  if (snapshot.empty) return null;
  
  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...convertTimestamps(docData.data()),
  } as CorporateBudget;
}

export async function getBudget(budgetId: string): Promise<CorporateBudget | null> {
  const docRef = doc(db, "corporate_budgets", budgetId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) return null;
  
  return {
    id: docSnap.id,
    ...convertTimestamps(docSnap.data()),
  } as CorporateBudget;
}

export async function createBudget(data: {
  corporateCustomerId: string;
  month: number;
  year: number;
  limit: number;
  alertThreshold?: number;
  notes?: string;
}): Promise<string> {
  const budgetData = {
    corporateCustomerId: data.corporateCustomerId,
    month: data.month,
    year: data.year,
    limit: data.limit,
    spent: 0,
    alertThreshold: data.alertThreshold ?? 80,
    alertSent: false,
    notes: data.notes ?? "",
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  
  const docRef = await addDoc(collection(db, "corporate_budgets"), budgetData);
  return docRef.id;
}

export async function updateBudgetSpent(budgetId: string, amount: number): Promise<void> {
  const budgetRef = doc(db, "corporate_budgets", budgetId);
  const budgetSnap = await getDoc(budgetRef);
  
  if (!budgetSnap.exists()) {
    throw new Error("Budget not found");
  }
  
  const budget = budgetSnap.data() as CorporateBudget;
  const newSpent = budget.spent + amount;
  const percentage = (newSpent / budget.limit) * 100;
  
  const updates: any = {
    spent: newSpent,
    updatedAt: Date.now(),
  };
  
  // Set alertSent flag if threshold exceeded
  if (percentage >= budget.alertThreshold && !budget.alertSent) {
    updates.alertSent = true;
  }
  
  await updateDoc(budgetRef, updates);
}

// ============================================================================
// ADMIN SUBSCRIPTION MANAGEMENT FUNCTIONS
// ============================================================================

export async function updateSubscriptionQuotas(
  subscriptionId: string,
  quotas: {
    remainingExteriorWashes?: number;
    remainingInteriorCleans?: number;
    remainingDetailings?: number;
  }
): Promise<void> {
  const docRef = doc(db, "customer_subscriptions", subscriptionId);
  await updateDoc(docRef, {
    ...quotas,
    updatedAt: Date.now(),
  });
}

export async function getSubscriptionUsageLogs(subscriptionId?: string): Promise<any[]> {
  let q;
  if (subscriptionId) {
    q = query(
      collection(db, "subscriptionUsageLog"),
      where("subscriptionId", "==", subscriptionId),
      orderBy("timestamp", "desc"),
      limit(100)
    );
  } else {
    q = query(
      collection(db, "subscriptionUsageLog"),
      orderBy("timestamp", "desc"),
      limit(200)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data()),
  }));
}

// ============================================================================
// SUBSCRIPTION ANALYTICS
// ============================================================================

export interface SubscriptionAnalytics {
  totalSubscribers: number;
  activeSubscribers: number;
  pausedSubscribers: number;
  cancelledSubscribers: number;
  expiredSubscribers: number;
  pendingPayment: number;
  mrr: number;
  churnRate: number;
  wastedQuotas: {
    totalExteriorWashes: number;
    totalInteriorCleans: number;
    totalDetailings: number;
    totalValue: number;
  };
  utilizationRate: {
    exterior: number;
    interior: number;
    detailing: number;
    overall: number;
  };
  expiringThisMonth: number;
  revenueThisMonth: number;
  revenueLast30Days: number;
}

export async function getSubscriptionAnalytics(): Promise<SubscriptionAnalytics> {
  const now = Date.now();
  const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59).getTime();

  const allSubs = await getAllCustomerSubscriptions();
  
  const packagesSnapshot = await getDocs(collection(db, "subscription_packages"));
  const packages = packagesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as SubscriptionPackage[];
  
  const packageMap = new Map(packages.map(p => [p.id, p]));

  const activeSubscribers = allSubs.filter(s => s.status === "active" && s.endDate > now).length;
  const pausedSubscribers = allSubs.filter(s => s.status === "paused").length;
  const cancelledSubscribers = allSubs.filter(s => s.status === "cancelled").length;
  const expiredSubscribers = allSubs.filter(s => s.endDate < now && s.status === "active").length;
  const pendingPayment = allSubs.filter(s => s.paymentStatus === "pending").length;

  let mrr = 0;
  allSubs.filter(s => s.status === "active" && s.endDate > now).forEach(sub => {
    const pkg = packageMap.get(sub.packageId);
    if (pkg) {
      const monthlyPrice = pkg.type === "monthly" ? pkg.price :
                          pkg.type === "quarterly" ? pkg.price / 3 :
                          pkg.type === "six_monthly" ? pkg.price / 6 :
                          pkg.type === "annual" ? pkg.price / 12 : pkg.price;
      mrr += monthlyPrice;
    }
  });

  const cancelledThisMonth = allSubs.filter(s => 
    s.status === "cancelled" && 
    s.cancelledAt && 
    s.cancelledAt >= startOfMonth && 
    s.cancelledAt <= endOfMonth
  ).length;
  
  const activeLastMonth = allSubs.filter(s => {
    const lastMonthStart = startOfMonth - (30 * 24 * 60 * 60 * 1000);
    return s.status === "active" && s.startDate < lastMonthStart;
  }).length;
  
  const churnRate = activeLastMonth > 0 ? (cancelledThisMonth / activeLastMonth) * 100 : 0;

  let wastedExterior = 0;
  let wastedInterior = 0;
  let wastedDetailing = 0;
  let wastedValue = 0;

  allSubs.filter(s => s.endDate < now).forEach(sub => {
    wastedExterior += sub.remainingExteriorWashes || 0;
    wastedInterior += sub.remainingInteriorCleans || 0;
    wastedDetailing += sub.remainingDetailings || 0;
    
    const pkg = packageMap.get(sub.packageId);
    if (pkg) {
      const totalQuotas = (pkg.exteriorWashCount || 0) + (pkg.interiorCleanCount || 0) + (pkg.detailingCount || 0);
      const remainingQuotas = (sub.remainingExteriorWashes || 0) + (sub.remainingInteriorCleans || 0) + (sub.remainingDetailings || 0);
      if (totalQuotas > 0) {
        wastedValue += (remainingQuotas / totalQuotas) * pkg.price;
      }
    }
  });

  let totalExteriorQuotas = 0;
  let usedExteriorQuotas = 0;
  let totalInteriorQuotas = 0;
  let usedInteriorQuotas = 0;
  let totalDetailingQuotas = 0;
  let usedDetailingQuotas = 0;

  allSubs.filter(s => s.status === "active" || s.endDate >= thirtyDaysAgo).forEach(sub => {
    totalExteriorQuotas += (sub.usedExteriorWashes || 0) + (sub.remainingExteriorWashes || 0);
    usedExteriorQuotas += sub.usedExteriorWashes || 0;
    
    totalInteriorQuotas += (sub.usedInteriorCleans || 0) + (sub.remainingInteriorCleans || 0);
    usedInteriorQuotas += sub.usedInteriorCleans || 0;
    
    totalDetailingQuotas += (sub.usedDetailings || 0) + (sub.remainingDetailings || 0);
    usedDetailingQuotas += sub.usedDetailings || 0;
  });

  const exteriorUtilization = totalExteriorQuotas > 0 ? (usedExteriorQuotas / totalExteriorQuotas) * 100 : 0;
  const interiorUtilization = totalInteriorQuotas > 0 ? (usedInteriorQuotas / totalInteriorQuotas) * 100 : 0;
  const detailingUtilization = totalDetailingQuotas > 0 ? (usedDetailingQuotas / totalDetailingQuotas) * 100 : 0;
  const totalQuotas = totalExteriorQuotas + totalInteriorQuotas + totalDetailingQuotas;
  const usedQuotas = usedExteriorQuotas + usedInteriorQuotas + usedDetailingQuotas;
  const overallUtilization = totalQuotas > 0 ? (usedQuotas / totalQuotas) * 100 : 0;

  const expiringThisMonth = allSubs.filter(s => 
    s.status === "active" && 
    s.endDate >= startOfMonth && 
    s.endDate <= endOfMonth
  ).length;

  const revenueThisMonth = allSubs
    .filter(s => s.startDate >= startOfMonth && s.startDate <= endOfMonth)
    .reduce((sum, sub) => sum + (sub.purchasePrice || 0), 0);

  const revenueLast30Days = allSubs
    .filter(s => s.startDate >= thirtyDaysAgo)
    .reduce((sum, sub) => sum + (sub.purchasePrice || 0), 0);

  return {
    totalSubscribers: allSubs.length,
    activeSubscribers,
    pausedSubscribers,
    cancelledSubscribers,
    expiredSubscribers,
    pendingPayment,
    mrr,
    churnRate,
    wastedQuotas: {
      totalExteriorWashes: wastedExterior,
      totalInteriorCleans: wastedInterior,
      totalDetailings: wastedDetailing,
      totalValue: wastedValue,
    },
    utilizationRate: {
      exterior: exteriorUtilization,
      interior: interiorUtilization,
      detailing: detailingUtilization,
      overall: overallUtilization,
    },
    expiringThisMonth,
    revenueThisMonth,
    revenueLast30Days,
  };
}

export interface WastedQuota {
  subscriptionId: string;
  userId: string;
  userName: string;
  userEmail: string;
  packageName: string;
  endDate: number;
  wastedExteriorWashes: number;
  wastedInteriorCleans: number;
  wastedDetailings: number;
  estimatedValue: number;
}

export async function getWastedQuotas(): Promise<WastedQuota[]> {
  const now = Date.now();
  const expiredSubs = await getDocs(
    query(
      collection(db, "customer_subscriptions"),
      where("endDate", "<", now),
      orderBy("endDate", "desc"),
      limit(100)
    )
  );

  const packagesSnapshot = await getDocs(collection(db, "subscription_packages"));
  const packages = packagesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as SubscriptionPackage[];
  const packageMap = new Map(packages.map(p => [p.id, p]));

  const wastedQuotas: WastedQuota[] = [];

  for (const docSnap of expiredSubs.docs) {
    const sub = { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as CustomerSubscription;
    
    const wastedExterior = sub.remainingExteriorWashes || 0;
    const wastedInterior = sub.remainingInteriorCleans || 0;
    const wastedDetailing = sub.remainingDetailings || 0;

    if (wastedExterior > 0 || wastedInterior > 0 || wastedDetailing > 0) {
      const userDoc = await getDoc(doc(db, "users", sub.userId));
      const userData = userDoc.exists() ? userDoc.data() : null;

      const pkg = packageMap.get(sub.packageId);
      const totalQuotas = pkg ? (pkg.exteriorWashCount || 0) + (pkg.interiorCleanCount || 0) + (pkg.detailingCount || 0) : 0;
      const remainingQuotas = wastedExterior + wastedInterior + wastedDetailing;
      const estimatedValue = pkg && totalQuotas > 0 ? (remainingQuotas / totalQuotas) * pkg.price : 0;

      wastedQuotas.push({
        subscriptionId: sub.id!,
        userId: sub.userId,
        userName: userData?.fullName || "Bilinmiyor",
        userEmail: userData?.email || "Bilinmiyor",
        packageName: pkg?.name || "Bilinmiyor",
        endDate: sub.endDate,
        wastedExteriorWashes: wastedExterior,
        wastedInteriorCleans: wastedInterior,
        wastedDetailings: wastedDetailing,
        estimatedValue,
      });
    }
  }

  return wastedQuotas;
}

// ============================================================================
// ADMIN MAINTENANCE FUNCTIONS
// ============================================================================

export interface FixWashResult {
  success: boolean;
  fixed: number;
  errors?: number;
  total?: number;
  message: string;
}

export async function fixWashCustomerIds(): Promise<FixWashResult> {
  console.log('[fixWashCustomerIds] Calling Cloud Function...');
  
  try {
    const fixFn = httpsCallable<{}, FixWashResult>(functions, 'fixWashCustomerIds');
    const result = await fixFn({});
    
    console.log('[fixWashCustomerIds] Result:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('[fixWashCustomerIds] Error:', error);
    throw error;
  }
}
